import { Content, Header, Page } from '@backstage/core-components';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LinkIcon from '@mui/icons-material/Link';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CodeEditor from '@uiw/react-textarea-code-editor';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { dump as dumpYaml } from 'js-yaml';

import { KubernetesConsoleApi, useKubernetesConsoleApi } from './api';
import { PodTerminal } from './PodTerminal';
import { TopologyPanel } from './TopologyPanel';
import { CatalogOwnership } from './CatalogOwnership';
import { CommandPalette, type ConsoleCommand } from './CommandPalette';
import { NoraDock, type NoraResourceSelection } from './NoraDock';
import {
  DISCOVERED_KIND_ID_PATTERN,
  buildDiscoveredKinds,
  discoveredKindId,
} from './discoveredKinds';
import { useApiResources } from './useApiResources';
import { useKubernetesWatch } from './useKubernetesWatch';
import { useWindowedRows } from './useWindowedRows';
import { KindPicker } from './KindPicker';
import { OperationsCenter } from './OperationsCenter';
import { EventsPanel } from './EventsPanel';
import { GuidedTour } from './GuidedTour';
import {
  STANDALONE_SECTIONS,
  isNavigationChange,
  parseConsoleUrl,
  serializeConsoleUrl,
  type ConsoleUrlState,
} from './consoleUrl';
import type {
  ApiResource,
  FavoriteResource,
  ImportResult,
  K8sAlert,
  K8sCluster,
  K8sEvent,
  K8sObject,
} from './types';

export const COLORS = {
  blue: '#8BC1F7',
  green: '#92C55E',
  amber: '#F4C145',
  red: '#F0561D',
  purple: '#B2A3FF',
  text: '#F0F0F0',
  muted: '#C7C7C7',
  border: '#4F5255',
  surface: '#1F1F1F',
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function ago(timestamp?: string): string {
  if (!timestamp) return '-';
  const seconds = Math.max(
    0,
    Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000),
  );
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function until(timestamp?: string): string {
  if (!timestamp) return '-';
  const seconds = Math.floor(
    (new Date(timestamp).getTime() - Date.now()) / 1000,
  );
  if (seconds <= 0) return 'expired';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

/** Parse a Kubernetes CPU quantity ("250m", "1", "12345678n") into cores. */
function parseCpu(quantity?: string): number {
  if (!quantity) return 0;
  const match = /^([0-9.]+)([num]?)$/.exec(quantity);
  if (!match) return 0;
  const scale: Record<string, number> = { n: 1e-9, u: 1e-6, m: 1e-3, '': 1 };
  return Number(match[1]) * scale[match[2]];
}

const MEMORY_UNITS: Record<string, number> = {
  Ki: 2 ** 10,
  Mi: 2 ** 20,
  Gi: 2 ** 30,
  Ti: 2 ** 40,
  k: 1e3,
  M: 1e6,
  G: 1e9,
  T: 1e12,
};

/** Parse a Kubernetes memory quantity ("128Mi", "2Gi", "1500k") into bytes. */
function parseMemory(quantity?: string): number {
  if (!quantity) return 0;
  const match = /^([0-9.]+)([A-Za-z]*)$/.exec(quantity);
  if (!match) return 0;
  return Number(match[1]) * (MEMORY_UNITS[match[2]] ?? 1);
}

function formatCpu(cores: number): string {
  if (!cores) return '0';
  if (cores < 1) return `${Math.max(1, Math.round(cores * 1000))}m`;
  return cores >= 10 ? cores.toFixed(1) : cores.toFixed(2);
}

function formatMemory(bytes: number): string {
  if (!bytes) return '0';
  if (bytes < 2 ** 20) return `${(bytes / 2 ** 10).toFixed(0)}Ki`;
  if (bytes < 2 ** 30) return `${(bytes / 2 ** 20).toFixed(0)}Mi`;
  return `${(bytes / 2 ** 30).toFixed(1)}Gi`;
}

/**
 * Build a "namespace/name" -> {cpu, memory} usage map from a
 * metrics.k8s.io PodMetricsList `items` array. Shared by the pods list
 * (cluster/namespace scoped) and the node/service pod tabs (which span
 * namespaces), so "top usage" sorting is consistent everywhere.
 */
function buildPodMetricsMap(
  items: any[],
): Record<string, { cpu: number; memory: number }> {
  const map: Record<string, { cpu: number; memory: number }> = {};
  for (const item of items) {
    const containers: any[] = item.containers ?? [];
    const key = `${item.metadata?.namespace}/${item.metadata?.name}`;
    map[key] = {
      cpu: containers.reduce((sum, c) => sum + parseCpu(c.usage?.cpu), 0),
      memory: containers.reduce(
        (sum, c) => sum + parseMemory(c.usage?.memory),
        0,
      ),
    };
  }
  return map;
}

/**
 * Sort fields shared by the node and service "Pods" tabs. Kept as a union
 * type (not derived from the table columns) since those two tables are
 * hand-rolled rather than driven by the generic Column[] catalogue.
 */
type PodSortField =
  | 'name'
  | 'namespace'
  | 'status'
  | 'restarts'
  | 'cpu'
  | 'memory'
  | 'ready'
  | 'podIP'
  | 'age'
  | 'owner';

const NUMERIC_POD_SORT_FIELDS = new Set<PodSortField>([
  'restarts',
  'cpu',
  'memory',
  'ready',
]);

function podSortValue(
  pod: K8sObject,
  usage: { cpu: number; memory: number },
  field: PodSortField,
): number | string {
  switch (field) {
    case 'name':
      return pod.metadata.name;
    case 'namespace':
      return pod.metadata.namespace ?? '';
    case 'status':
      return podStatus(pod).label;
    case 'restarts':
      return podRestarts(pod);
    case 'cpu':
      return usage.cpu;
    case 'memory':
      return usage.memory;
    case 'ready': {
      const statuses = pod.status?.containerStatuses ?? [];
      return statuses.filter((c: any) => c.ready).length;
    }
    case 'podIP':
      return pod.status?.podIP ?? '';
    case 'age':
      return pod.metadata.creationTimestamp ?? '';
    case 'owner':
      return podOwner(pod);
    default:
      return pod.metadata.name;
  }
}

function sortPodsWithUsage<
  T extends { pod: K8sObject; usage: { cpu: number; memory: number } },
>(list: T[], field: PodSortField, dir: 'asc' | 'desc'): T[] {
  const sorted = [...list];
  sorted.sort((a, b) => {
    const av = podSortValue(a.pod, a.usage, field);
    const bv = podSortValue(b.pod, b.usage, field);
    const result =
      typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv));
    return dir === 'asc' ? result : -result;
  });
  return sorted;
}

/** Owner reference summary ("ReplicaSet/foo-abc123") for the pods pages. */
function podOwner(pod: K8sObject): string {
  const owner = (pod.metadata as any).ownerReferences?.[0];
  return owner ? `${owner.kind}/${owner.name}` : '-';
}

/**
 * Build a node name -> {cpu, memory} usage map from a metrics.k8s.io
 * NodeMetricsList `items` array, for the Nodes list's CPU/Memory columns.
 */
function buildNodeMetricsMap(
  items: any[],
): Record<string, { cpu: number; memory: number }> {
  const map: Record<string, { cpu: number; memory: number }> = {};
  for (const item of items) {
    const name = item.metadata?.name;
    if (!name) continue;
    map[name] = {
      cpu: parseCpu(item.usage?.cpu),
      memory: parseMemory(item.usage?.memory),
    };
  }
  return map;
}

function podStatus(pod: K8sObject): { label: string; color: string } {
  const phase = pod.status?.phase ?? 'Unknown';
  if (pod.metadata.annotations === undefined && phase === 'Unknown') {
    return { label: phase, color: COLORS.muted };
  }
  const waiting = (pod.status?.containerStatuses ?? [])
    .map((c: any) => c.state?.waiting?.reason)
    .find(Boolean);
  if (pod.metadata && (pod as any).metadata.deletionTimestamp) {
    return { label: 'Terminating', color: COLORS.amber };
  }
  if (waiting) {
    return {
      label: waiting,
      color: waiting === 'ContainerCreating' ? COLORS.amber : COLORS.red,
    };
  }
  if (phase === 'Running') {
    const ready = (pod.status?.containerStatuses ?? []).every(
      (c: any) => c.ready,
    );
    return ready
      ? { label: 'Running', color: COLORS.green }
      : { label: 'NotReady', color: COLORS.amber };
  }
  if (phase === 'Succeeded') return { label: phase, color: COLORS.blue };
  if (phase === 'Failed') return { label: phase, color: COLORS.red };
  if (phase === 'Pending') return { label: phase, color: COLORS.amber };
  return { label: phase, color: COLORS.muted };
}

function podReady(pod: K8sObject): string {
  const statuses = pod.status?.containerStatuses ?? [];
  const ready = statuses.filter((c: any) => c.ready).length;
  return `${ready}/${statuses.length || pod.spec?.containers?.length || 0}`;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return '-';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${
    units[unit]
  }`;
}

interface PvcUsageStats {
  usedBytes: number;
  capacityBytes: number;
}

/**
 * PVC disk usage is not part of the PVC object; kubelets expose it via the
 * node stats summary. Query the nodes that run pods mounting a PVC in this
 * namespace and collect the per-volume numbers. Needs `get nodes/proxy` —
 * nodes we cannot read are skipped silently so the page still works for
 * users without that permission.
 */
async function fetchPvcUsageMap(
  api: KubernetesConsoleApi,
  cluster: string,
  namespace: string,
): Promise<Record<string, PvcUsageStats>> {
  const usage: Record<string, PvcUsageStats> = {};
  const podList = await api.listAll(
    cluster,
    `/api/v1/namespaces/${encodeURIComponent(namespace)}/pods`,
  );
  const nodes = new Set<string>();
  for (const pod of podList.items) {
    const mountsPvc = (pod.spec?.volumes ?? []).some(
      (volume: any) => volume.persistentVolumeClaim,
    );
    if (mountsPvc && pod.spec?.nodeName) nodes.add(pod.spec.nodeName);
  }
  await Promise.all(
    [...nodes].map(async node => {
      try {
        const summary = await api.proxy<any>(
          cluster,
          `/api/v1/nodes/${encodeURIComponent(node)}/proxy/stats/summary`,
        );
        for (const pod of summary.pods ?? []) {
          for (const volume of pod.volume ?? []) {
            if (
              volume.pvcRef?.namespace === namespace &&
              typeof volume.usedBytes === 'number'
            ) {
              usage[volume.pvcRef.name] = {
                usedBytes: volume.usedBytes,
                capacityBytes: volume.capacityBytes ?? 0,
              };
            }
          }
        }
      } catch {
        /* no nodes/proxy access or node unreachable */
      }
    }),
  );
  return usage;
}

function podRestarts(pod: K8sObject): number {
  return (pod.status?.containerStatuses ?? []).reduce(
    (sum: number, c: any) => sum + (c.restartCount ?? 0),
    0,
  );
}

function readyColor(ready: number, desired: number): string {
  if (desired === 0) return COLORS.muted;
  return ready >= desired ? COLORS.green : COLORS.amber;
}

function toYaml(value: unknown): string {
  return dumpYaml(value, {
    lineWidth: -1,
    noRefs: true,
    sortKeys: false,
  });
}

function redactSecret(object: K8sObject): K8sObject {
  if (object.kind !== 'Secret' || !object.data) return object;
  const data: Record<string, string> = {};
  for (const key of Object.keys(object.data)) data[key] = '<redacted>';
  return { ...object, data };
}

/**
 * The manifest the YAML editor starts from: the live object without
 * managedFields (server bookkeeping that drowns the useful parts). Secrets
 * keep their real data here — saving a redacted manifest would corrupt them.
 */
function editableYaml(object: K8sObject): string {
  const { managedFields: _managedFields, ...metadata } = object.metadata as any;
  return toYaml({ ...object, metadata });
}

interface EnvVar {
  name: string;
  value?: string;
  valueFrom?: any;
}

/** Containers to show on the Environment tab: a pod's own spec, or a
 * workload's pod template for Deployment/StatefulSet/DaemonSet. */
function envContainers(object: K8sObject): { name: string; env: EnvVar[] }[] {
  const containers =
    object.spec?.containers ?? object.spec?.template?.spec?.containers ?? [];
  return containers.map((container: any) => ({
    name: container.name,
    env: (container.env ?? []) as EnvVar[],
  }));
}

/** Human-readable description of an env var sourced from a ConfigMap,
 * Secret, or the pod/container's own fields, rather than a literal value. */
function envValueFromLabel(valueFrom: any): string {
  if (!valueFrom) return '-';
  if (valueFrom.configMapKeyRef) {
    return `ConfigMap ${valueFrom.configMapKeyRef.name} · ${valueFrom.configMapKeyRef.key}`;
  }
  if (valueFrom.secretKeyRef) {
    return `Secret ${valueFrom.secretKeyRef.name} · ${valueFrom.secretKeyRef.key}`;
  }
  if (valueFrom.fieldRef) return `Field ${valueFrom.fieldRef.fieldPath}`;
  if (valueFrom.resourceFieldRef) {
    return `Resource ${valueFrom.resourceFieldRef.resource}`;
  }
  return '-';
}

// ─── kind catalogue (OpenShift console-style sections) ───────────────────────

interface ColumnContext {
  pvcUsage?: Record<string, PvcUsageStats>;
  podMetrics?: Record<string, { cpu: number; memory: number }>;
  nodeMetrics?: Record<string, { cpu: number; memory: number }>;
  /** Sum of each Karpenter NodePool's nodes' current CPU usage (cores), keyed by NodePool name. */
  nodePoolCpuUsage?: Record<string, number>;
  /** Opens the Service detail drawer for a backend referenced by name (e.g. from an HTTPRoute). */
  onNavigateToService?: (namespace: string, name: string) => void;
  /** Opens the Node detail drawer for a node referenced by name (e.g. from a Pod's Node column). */
  onNavigateToNode?: (name: string) => void;
  /** Opens the instance list for a discovered kind (e.g. from a CRD row). */
  onNavigateToKind?: (plural: string, group: string) => void;
}

interface Column {
  label: string;
  render: (object: K8sObject, context?: ColumnContext) => React.ReactNode;
  /** When set, the column header becomes clickable and sorts by this value. */
  sortValue?: (object: K8sObject, context?: ColumnContext) => number | string;
}

interface KindDef {
  id: string;
  label: string;
  kind: string;
  plural: string;
  apiBase: string;
  group: string;
  /** false for cluster-scoped kinds (Nodes, ClusterRoles, NodePools, …). */
  namespaced?: boolean;
  columns: Column[];
  /** True for kinds that came from API discovery rather than the catalogue. */
  discovered?: boolean;
}

function chip(label: string, color: string) {
  return <StatusChip label={label} color={color} />;
}

function conditionChip(object: K8sObject, type = 'Ready') {
  const condition = (object.status?.conditions ?? []).find(
    (c: any) => c.type === type,
  );
  if (!condition) return chip('Unknown', COLORS.muted);
  if (condition.status === 'True') return chip(type, COLORS.green);
  return chip(condition.reason ?? `Not${type}`, COLORS.amber);
}

const AGE_COLUMN: Column = {
  label: 'Age',
  render: object => ago(object.metadata.creationTimestamp),
  sortValue: object => object.metadata.creationTimestamp ?? '',
};

/** Render hostnames as clickable https:// links; wildcard hosts stay plain. */
function hostLinks(hosts: string[]) {
  const named = hosts.filter(Boolean);
  if (!named.length) return '*';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1 }}>
      {named.map(host =>
        host.includes('*') ? (
          <Typography key={host} component="span" sx={{ fontSize: 'inherit' }}>
            {host}
          </Typography>
        ) : (
          <Link
            key={host}
            href={`https://${host}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={event => event.stopPropagation()}
            sx={{ color: COLORS.blue, fontSize: 'inherit' }}
          >
            {host}
          </Link>
        ),
      )}
    </Box>
  );
}

const KINDS: { section: string; items: KindDef[] }[] = [
  {
    section: 'Workloads',
    items: [
      {
        id: 'pods',
        label: 'Pods',
        kind: 'Pod',
        plural: 'pods',
        apiBase: '/api/v1',
        group: '',
        columns: [
          {
            label: 'Status',
            render: pod => {
              const { label, color } = podStatus(pod);
              return chip(label, color);
            },
            sortValue: pod => podStatus(pod).label,
          },
          {
            label: 'Owner',
            render: pod => podOwner(pod),
            sortValue: pod => podOwner(pod),
          },
          {
            label: 'Ready',
            render: pod => podReady(pod),
            sortValue: pod => podReady(pod),
          },
          {
            label: 'Restarts',
            render: pod => podRestarts(pod),
            sortValue: pod => podRestarts(pod),
          },
          {
            label: 'CPU',
            render: (pod, context) => {
              const usage =
                context?.podMetrics?.[
                  `${pod.metadata.namespace}/${pod.metadata.name}`
                ];
              return usage ? formatCpu(usage.cpu) : '-';
            },
            sortValue: (pod, context) =>
              context?.podMetrics?.[
                `${pod.metadata.namespace}/${pod.metadata.name}`
              ]?.cpu ?? 0,
          },
          {
            label: 'Memory',
            render: (pod, context) => {
              const usage =
                context?.podMetrics?.[
                  `${pod.metadata.namespace}/${pod.metadata.name}`
                ];
              return usage ? formatMemory(usage.memory) : '-';
            },
            sortValue: (pod, context) =>
              context?.podMetrics?.[
                `${pod.metadata.namespace}/${pod.metadata.name}`
              ]?.memory ?? 0,
          },
          {
            label: 'Node',
            render: (pod, context) => {
              const nodeName = pod.spec?.nodeName;
              if (!nodeName) return '-';
              return (
                <Link
                  component="button"
                  variant="body2"
                  onClick={event => {
                    event.stopPropagation();
                    context?.onNavigateToNode?.(nodeName);
                  }}
                  sx={{ color: COLORS.blue, fontSize: 'inherit' }}
                >
                  {nodeName}
                </Link>
              );
            },
            sortValue: pod => pod.spec?.nodeName ?? '',
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'deployments',
        label: 'Deployments',
        kind: 'Deployment',
        plural: 'deployments',
        apiBase: '/apis/apps/v1',
        group: 'apps',
        columns: [
          {
            label: 'Ready',
            render: d => {
              const ready = d.status?.readyReplicas ?? 0;
              const desired = d.spec?.replicas ?? 0;
              return chip(`${ready}/${desired}`, readyColor(ready, desired));
            },
          },
          { label: 'Up to date', render: d => d.status?.updatedReplicas ?? 0 },
          { label: 'Available', render: d => d.status?.availableReplicas ?? 0 },
          AGE_COLUMN,
        ],
      },
      {
        id: 'statefulsets',
        label: 'StatefulSets',
        kind: 'StatefulSet',
        plural: 'statefulsets',
        apiBase: '/apis/apps/v1',
        group: 'apps',
        columns: [
          {
            label: 'Ready',
            render: s => {
              const ready = s.status?.readyReplicas ?? 0;
              const desired = s.spec?.replicas ?? 0;
              return chip(`${ready}/${desired}`, readyColor(ready, desired));
            },
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'replicasets',
        label: 'ReplicaSets',
        kind: 'ReplicaSet',
        plural: 'replicasets',
        apiBase: '/apis/apps/v1',
        group: 'apps',
        columns: [
          {
            label: 'Ready',
            render: rs => {
              const ready = rs.status?.readyReplicas ?? 0;
              const desired = rs.spec?.replicas ?? 0;
              return chip(`${ready}/${desired}`, readyColor(ready, desired));
            },
          },
          {
            label: 'Owner',
            render: rs => {
              const owner = (rs.metadata as any).ownerReferences?.[0];
              return owner ? `${owner.kind}/${owner.name}` : '-';
            },
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'daemonsets',
        label: 'DaemonSets',
        kind: 'DaemonSet',
        plural: 'daemonsets',
        apiBase: '/apis/apps/v1',
        group: 'apps',
        columns: [
          {
            label: 'Ready',
            render: d => {
              const ready = d.status?.numberReady ?? 0;
              const desired = d.status?.desiredNumberScheduled ?? 0;
              return chip(`${ready}/${desired}`, readyColor(ready, desired));
            },
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'jobs',
        label: 'Jobs',
        kind: 'Job',
        plural: 'jobs',
        apiBase: '/apis/batch/v1',
        group: 'batch',
        columns: [
          {
            label: 'Status',
            render: job => {
              if (job.status?.succeeded) return chip('Complete', COLORS.green);
              if (job.status?.failed) return chip('Failed', COLORS.red);
              if (job.status?.active) return chip('Active', COLORS.amber);
              return chip('Pending', COLORS.muted);
            },
          },
          {
            label: 'Completions',
            render: job =>
              `${job.status?.succeeded ?? 0}/${job.spec?.completions ?? 1}`,
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'cronjobs',
        label: 'CronJobs',
        kind: 'CronJob',
        plural: 'cronjobs',
        apiBase: '/apis/batch/v1',
        group: 'batch',
        columns: [
          { label: 'Schedule', render: c => c.spec?.schedule ?? '-' },
          {
            label: 'Suspend',
            render: c =>
              c.spec?.suspend
                ? chip('Suspended', COLORS.amber)
                : chip('Active', COLORS.green),
          },
          {
            label: 'Last schedule',
            render: c => ago(c.status?.lastScheduleTime),
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'horizontalpodautoscalers',
        label: 'HorizontalPodAutoscalers',
        kind: 'HorizontalPodAutoscaler',
        plural: 'horizontalpodautoscalers',
        apiBase: '/apis/autoscaling/v2',
        group: 'autoscaling',
        columns: [
          {
            label: 'Target',
            render: hpa => {
              const target = (hpa.spec as any)?.scaleTargetRef;
              return target ? `${target.kind}/${target.name}` : '-';
            },
          },
          {
            label: 'Min / Max',
            render: hpa =>
              `${hpa.spec?.minReplicas ?? 1} / ${hpa.spec?.maxReplicas ?? '-'}`,
          },
          {
            label: 'Replicas',
            render: hpa => `${hpa.status?.currentReplicas ?? 0}`,
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'poddisruptionbudgets',
        label: 'PodDisruptionBudgets',
        kind: 'PodDisruptionBudget',
        plural: 'poddisruptionbudgets',
        apiBase: '/apis/policy/v1',
        group: 'policy',
        columns: [
          {
            label: 'Min available',
            render: pdb => `${(pdb.spec as any)?.minAvailable ?? '-'}`,
          },
          {
            label: 'Max unavailable',
            render: pdb => `${(pdb.spec as any)?.maxUnavailable ?? '-'}`,
          },
          {
            label: 'Allowed disruptions',
            render: pdb => `${(pdb.status as any)?.disruptionsAllowed ?? 0}`,
          },
          AGE_COLUMN,
        ],
      },
    ],
  },
  {
    section: 'Networking',
    items: [
      {
        id: 'services',
        label: 'Services',
        kind: 'Service',
        plural: 'services',
        apiBase: '/api/v1',
        group: '',
        columns: [
          { label: 'Type', render: s => s.spec?.type ?? '-' },
          { label: 'Cluster IP', render: s => s.spec?.clusterIP ?? '-' },
          {
            label: 'Ports',
            render: s =>
              (s.spec?.ports ?? [])
                .map(
                  (p: any) =>
                    `${p.port}${p.protocol === 'UDP' ? '/UDP' : ''}${
                      p.nodePort ? `:${p.nodePort}` : ''
                    }`,
                )
                .join(', ') || '-',
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'ingresses',
        label: 'Ingresses',
        kind: 'Ingress',
        plural: 'ingresses',
        apiBase: '/apis/networking.k8s.io/v1',
        group: 'networking.k8s.io',
        columns: [
          {
            label: 'Hosts',
            render: ingress =>
              hostLinks(
                (ingress.spec?.rules ?? []).map((rule: any) => rule.host),
              ),
          },
          {
            label: 'Address',
            render: ingress =>
              (ingress.status?.loadBalancer?.ingress ?? [])
                .map((address: any) => address.ip ?? address.hostname)
                .join(', ') || '-',
          },
          { label: 'Class', render: i => i.spec?.ingressClassName ?? '-' },
          AGE_COLUMN,
        ],
      },
      {
        id: 'routes',
        label: 'Routes',
        kind: 'Route',
        plural: 'routes',
        apiBase: '/apis/route.openshift.io/v1',
        group: 'route.openshift.io',
        columns: [
          {
            label: 'Host',
            render: route =>
              route.spec?.host ? hostLinks([route.spec.host]) : '-',
          },
          {
            label: 'Service',
            render: route => route.spec?.to?.name ?? '-',
          },
          {
            label: 'TLS',
            render: route => route.spec?.tls?.termination ?? '-',
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'gateways',
        label: 'Gateways',
        kind: 'Gateway',
        plural: 'gateways',
        apiBase: '/apis/gateway.networking.k8s.io/v1',
        group: 'gateway.networking.k8s.io',
        columns: [
          { label: 'Class', render: g => g.spec?.gatewayClassName ?? '-' },
          {
            label: 'Address',
            render: g =>
              ((g.status as any)?.addresses ?? [])
                .map((address: any) => address.value)
                .join(', ') || '-',
          },
          { label: 'Programmed', render: g => conditionChip(g, 'Programmed') },
          AGE_COLUMN,
        ],
      },
      {
        id: 'httproutes',
        label: 'HTTPRoutes',
        kind: 'HTTPRoute',
        plural: 'httproutes',
        apiBase: '/apis/gateway.networking.k8s.io/v1',
        group: 'gateway.networking.k8s.io',
        columns: [
          {
            label: 'Hostnames',
            render: route => hostLinks((route.spec as any)?.hostnames ?? []),
          },
          {
            label: 'Gateways',
            render: route =>
              ((route.spec as any)?.parentRefs ?? [])
                .map((parent: any) => parent.name)
                .join(', ') || '-',
          },
          {
            label: 'Services',
            render: (route, context) => {
              const names: string[] = ((route.spec as any)?.rules ?? [])
                .flatMap((rule: any) => rule.backendRefs ?? [])
                .map((backend: any) => backend.name)
                .filter(Boolean);
              const unique = [...new Set(names)];
              if (!unique.length) return '-';
              const routeNamespace = route.metadata.namespace ?? '';
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1 }}>
                  {unique.map((serviceName, index) =>
                    context?.onNavigateToService ? (
                      <Link
                        key={serviceName}
                        component="button"
                        onClick={event => {
                          event.stopPropagation();
                          context.onNavigateToService!(
                            routeNamespace,
                            serviceName,
                          );
                        }}
                        sx={{ color: COLORS.blue, fontSize: 'inherit' }}
                      >
                        {serviceName}
                        {index < unique.length - 1 ? ',' : ''}
                      </Link>
                    ) : (
                      <Typography
                        key={serviceName}
                        component="span"
                        sx={{ fontSize: 'inherit' }}
                      >
                        {serviceName}
                        {index < unique.length - 1 ? ',' : ''}
                      </Typography>
                    ),
                  )}
                </Box>
              );
            },
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'networkpolicies',
        label: 'NetworkPolicies',
        kind: 'NetworkPolicy',
        plural: 'networkpolicies',
        apiBase: '/apis/networking.k8s.io/v1',
        group: 'networking.k8s.io',
        columns: [
          {
            label: 'Pod selector',
            render: np =>
              Object.entries(np.spec?.podSelector?.matchLabels ?? {})
                .map(([key, value]) => `${key}=${value}`)
                .join(', ') || 'All pods',
          },
          AGE_COLUMN,
        ],
      },
    ],
  },
  {
    section: 'Storage',
    items: [
      {
        id: 'persistentvolumeclaims',
        label: 'PersistentVolumeClaims',
        kind: 'PersistentVolumeClaim',
        plural: 'persistentvolumeclaims',
        apiBase: '/api/v1',
        group: '',
        columns: [
          {
            label: 'Status',
            render: pvc =>
              chip(
                pvc.status?.phase ?? 'Unknown',
                pvc.status?.phase === 'Bound' ? COLORS.green : COLORS.amber,
              ),
          },
          {
            label: 'Capacity',
            render: pvc =>
              pvc.status?.capacity?.storage ??
              pvc.spec?.resources?.requests?.storage ??
              '-',
          },
          {
            label: 'Used',
            render: (pvc, context) => {
              const usage = context?.pvcUsage?.[pvc.metadata.name];
              if (!usage) return '-';
              const percent = usage.capacityBytes
                ? Math.min(
                    100,
                    Math.round((usage.usedBytes / usage.capacityBytes) * 100),
                  )
                : 0;
              return (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 64,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: COLORS.border,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${percent}%`,
                        height: '100%',
                        bgcolor:
                          percent >= 90
                            ? COLORS.red
                            : percent >= 75
                            ? COLORS.amber
                            : COLORS.blue,
                      }}
                    />
                  </Box>
                  <span>{`${formatBytes(usage.usedBytes)} (${percent}%)`}</span>
                </Stack>
              );
            },
          },
          {
            label: 'Storage class',
            render: pvc => pvc.spec?.storageClassName ?? '-',
          },
          AGE_COLUMN,
        ],
      },
    ],
  },
  {
    section: 'Config',
    items: [
      {
        id: 'configmaps',
        label: 'ConfigMaps',
        kind: 'ConfigMap',
        plural: 'configmaps',
        apiBase: '/api/v1',
        group: '',
        columns: [
          {
            label: 'Keys',
            render: cm => Object.keys(cm.data ?? {}).length,
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'secrets',
        label: 'Secrets',
        kind: 'Secret',
        plural: 'secrets',
        apiBase: '/api/v1',
        group: '',
        columns: [
          { label: 'Type', render: s => s.type ?? '-' },
          {
            label: 'Keys',
            render: s => Object.keys(s.data ?? {}).length,
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'secretproviderclasses',
        label: 'SecretProviderClasses',
        kind: 'SecretProviderClass',
        plural: 'secretproviderclasses',
        apiBase: '/apis/secrets-store.csi.x-k8s.io/v1',
        group: 'secrets-store.csi.x-k8s.io',
        columns: [
          { label: 'Provider', render: s => s.spec?.provider ?? '-' },
          {
            label: 'Synced secrets',
            render: s => (s.spec?.secretObjects ?? []).length,
          },
          AGE_COLUMN,
        ],
      },
    ],
  },
  {
    section: 'Certificates',
    items: [
      {
        id: 'certificates',
        label: 'Certificates',
        kind: 'Certificate',
        plural: 'certificates',
        apiBase: '/apis/cert-manager.io/v1',
        group: 'cert-manager.io',
        columns: [
          { label: 'Ready', render: c => conditionChip(c) },
          { label: 'Secret', render: c => c.spec?.secretName ?? '-' },
          {
            label: 'Expires',
            render: c => {
              if (!c.status?.notAfter) return '-';
              const remaining = until(c.status.notAfter);
              return remaining === 'expired'
                ? chip('Expired', COLORS.red)
                : `in ${remaining}`;
            },
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'challenges',
        label: 'Challenges',
        kind: 'Challenge',
        plural: 'challenges',
        apiBase: '/apis/acme.cert-manager.io/v1',
        group: 'acme.cert-manager.io',
        columns: [
          {
            label: 'State',
            render: ch => {
              const state = ch.status?.state ?? 'pending';
              const color =
                state === 'valid'
                  ? COLORS.green
                  : ['errored', 'invalid', 'expired'].includes(state)
                  ? COLORS.red
                  : COLORS.amber;
              return chip(state, color);
            },
          },
          { label: 'Domain', render: ch => ch.spec?.dnsName ?? '-' },
          AGE_COLUMN,
        ],
      },
    ],
  },
  {
    section: 'Argo CD',
    items: [
      {
        id: 'applications',
        label: 'Applications',
        kind: 'Application',
        plural: 'applications',
        apiBase: '/apis/argoproj.io/v1alpha1',
        group: 'argoproj.io',
        columns: [
          {
            label: 'Sync',
            render: a =>
              chip(
                a.status?.sync?.status ?? 'Unknown',
                a.status?.sync?.status === 'Synced'
                  ? COLORS.green
                  : COLORS.amber,
              ),
          },
          {
            label: 'Health',
            render: a => {
              const health = a.status?.health?.status ?? 'Unknown';
              const color =
                health === 'Healthy'
                  ? COLORS.green
                  : ['Degraded', 'Missing'].includes(health)
                  ? COLORS.red
                  : COLORS.amber;
              return chip(health, color);
            },
          },
          {
            label: 'Destination',
            render: a => a.spec?.destination?.namespace ?? '-',
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'applicationsets',
        label: 'ApplicationSets',
        kind: 'ApplicationSet',
        plural: 'applicationsets',
        apiBase: '/apis/argoproj.io/v1alpha1',
        group: 'argoproj.io',
        columns: [
          {
            label: 'Generators',
            render: a => (a.spec?.generators ?? []).length,
          },
          AGE_COLUMN,
        ],
      },
    ],
  },
  {
    section: 'Messaging',
    items: [
      {
        id: 'activemqartemises',
        label: 'AMQ Brokers',
        kind: 'ActiveMQArtemis',
        plural: 'activemqartemises',
        apiBase: '/apis/broker.amq.io/v1beta1',
        group: 'broker.amq.io',
        columns: [
          { label: 'Ready', render: b => conditionChip(b) },
          {
            label: 'Pods ready',
            render: b =>
              `${(b.status?.podStatus?.ready ?? []).length}/${
                b.spec?.deploymentPlan?.size ?? 1
              }`,
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'hawtios',
        label: 'Hawtio consoles',
        kind: 'Hawtio',
        plural: 'hawtios',
        apiBase: '/apis/hawt.io/v1',
        group: 'hawt.io',
        columns: [
          {
            label: 'Phase',
            render: h =>
              chip(
                h.status?.phase ?? 'Unknown',
                h.status?.phase === 'Deployed' ? COLORS.green : COLORS.amber,
              ),
          },
          { label: 'URL', render: h => h.status?.URL ?? '-' },
          AGE_COLUMN,
        ],
      },
    ],
  },
  {
    section: 'User management',
    items: [
      {
        id: 'roles',
        label: 'Roles',
        kind: 'Role',
        plural: 'roles',
        apiBase: '/apis/rbac.authorization.k8s.io/v1',
        group: 'rbac.authorization.k8s.io',
        columns: [
          {
            label: 'Rules',
            render: role => ((role as any).rules ?? []).length,
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'rolebindings',
        label: 'RoleBindings',
        kind: 'RoleBinding',
        plural: 'rolebindings',
        apiBase: '/apis/rbac.authorization.k8s.io/v1',
        group: 'rbac.authorization.k8s.io',
        columns: [
          {
            label: 'Role',
            render: binding => {
              const ref = (binding as any).roleRef;
              return ref ? `${ref.kind}/${ref.name}` : '-';
            },
          },
          {
            label: 'Subjects',
            render: binding =>
              ((binding as any).subjects ?? [])
                .map((subject: any) => subject.name)
                .join(', ') || '-',
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'clusterroles',
        label: 'ClusterRoles',
        kind: 'ClusterRole',
        plural: 'clusterroles',
        apiBase: '/apis/rbac.authorization.k8s.io/v1',
        group: 'rbac.authorization.k8s.io',
        namespaced: false,
        columns: [
          {
            label: 'Rules',
            render: role => ((role as any).rules ?? []).length,
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'clusterrolebindings',
        label: 'ClusterRoleBindings',
        kind: 'ClusterRoleBinding',
        plural: 'clusterrolebindings',
        apiBase: '/apis/rbac.authorization.k8s.io/v1',
        group: 'rbac.authorization.k8s.io',
        namespaced: false,
        columns: [
          {
            label: 'Role',
            render: binding => {
              const ref = (binding as any).roleRef;
              return ref ? ref.name : '-';
            },
          },
          {
            label: 'Subjects',
            render: binding =>
              ((binding as any).subjects ?? [])
                .map((subject: any) => subject.name)
                .join(', ') || '-',
          },
          AGE_COLUMN,
        ],
      },
    ],
  },
  {
    section: 'Cluster',
    items: [
      {
        id: 'nodes',
        label: 'Nodes',
        kind: 'Node',
        plural: 'nodes',
        apiBase: '/api/v1',
        group: '',
        namespaced: false,
        columns: [
          {
            label: 'Status',
            render: node => conditionChip(node),
            sortValue: node =>
              (node.status?.conditions ?? []).find(
                (c: any) => c.type === 'Ready',
              )?.status === 'True'
                ? 1
                : 0,
          },
          {
            label: 'Pool',
            render: node =>
              node.metadata.labels?.['karpenter.sh/nodepool'] ??
              node.metadata.labels?.agentpool ??
              '-',
            sortValue: node =>
              node.metadata.labels?.['karpenter.sh/nodepool'] ??
              node.metadata.labels?.agentpool ??
              '',
          },
          {
            label: 'Instance type',
            render: node =>
              node.metadata.labels?.['node.kubernetes.io/instance-type'] ?? '-',
            sortValue: node =>
              node.metadata.labels?.['node.kubernetes.io/instance-type'] ?? '',
          },
          {
            label: 'CPU',
            render: (node, context) => {
              const usage = context?.nodeMetrics?.[node.metadata.name];
              const capacity = parseCpu(
                node.status?.capacity?.cpu ?? node.status?.allocatable?.cpu,
              );
              if (!usage) return capacity ? `-/${formatCpu(capacity)}` : '-';
              return `${formatCpu(usage.cpu)}/${formatCpu(capacity)}`;
            },
            sortValue: (node, context) =>
              context?.nodeMetrics?.[node.metadata.name]?.cpu ?? 0,
          },
          {
            label: 'Memory',
            render: (node, context) => {
              const usage = context?.nodeMetrics?.[node.metadata.name];
              const capacity = parseMemory(
                node.status?.capacity?.memory ??
                  node.status?.allocatable?.memory,
              );
              if (!usage) return capacity ? `-/${formatMemory(capacity)}` : '-';
              return `${formatMemory(usage.memory)}/${formatMemory(capacity)}`;
            },
            sortValue: (node, context) =>
              context?.nodeMetrics?.[node.metadata.name]?.memory ?? 0,
          },
          {
            label: 'Version',
            render: node => node.status?.nodeInfo?.kubeletVersion ?? '-',
            sortValue: node => node.status?.nodeInfo?.kubeletVersion ?? '',
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'customresourcedefinitions',
        label: 'CustomResourceDefinitions',
        kind: 'CustomResourceDefinition',
        plural: 'customresourcedefinitions',
        apiBase: '/apis/apiextensions.k8s.io/v1',
        group: 'apiextensions.k8s.io',
        namespaced: false,
        columns: [
          {
            label: 'Kind',
            render: (crd, context) => {
              const kind = crd.spec?.names?.kind;
              if (!kind) return '-';
              const open = context?.onNavigateToKind;
              if (!open) return kind;
              return (
                <Link
                  component="button"
                  onClick={event => {
                    event.stopPropagation();
                    open(crd.spec.names.plural, crd.spec.group);
                  }}
                  sx={{ color: COLORS.blue, fontSize: 'inherit' }}
                >
                  {kind}
                </Link>
              );
            },
            sortValue: crd => crd.spec?.names?.kind ?? '',
          },
          {
            label: 'Group',
            render: crd => crd.spec?.group ?? '-',
            sortValue: crd => crd.spec?.group ?? '',
          },
          {
            label: 'Version',
            render: crd => storedCrdVersion(crd) || '-',
            sortValue: crd => storedCrdVersion(crd),
          },
          {
            label: 'Scope',
            render: crd => crd.spec?.scope ?? '-',
            sortValue: crd => crd.spec?.scope ?? '',
          },
          {
            label: 'Established',
            render: crd => conditionChip(crd, 'Established'),
          },
          AGE_COLUMN,
        ],
      },
    ],
  },
  {
    section: 'Karpenter',
    items: [
      {
        id: 'nodepools',
        label: 'NodePools',
        kind: 'NodePool',
        plural: 'nodepools',
        apiBase: '/apis/karpenter.sh/v1',
        group: 'karpenter.sh',
        namespaced: false,
        columns: [
          { label: 'Ready', render: pool => conditionChip(pool) },
          {
            label: 'NodeClass',
            render: pool =>
              pool.spec?.template?.spec?.nodeClassRef?.name ?? '-',
          },
          {
            label: 'CPU used',
            render: (pool, context) => {
              const used = context?.nodePoolCpuUsage?.[pool.metadata.name];
              return used !== undefined ? formatCpu(used) : '-';
            },
            sortValue: (pool, context) =>
              context?.nodePoolCpuUsage?.[pool.metadata.name] ?? 0,
          },
          { label: 'CPU limit', render: pool => pool.spec?.limits?.cpu ?? '-' },
          {
            label: 'Nodes',
            render: pool => pool.status?.resources?.nodes ?? '-',
          },
          AGE_COLUMN,
        ],
      },
      {
        id: 'nodeclaims',
        label: 'NodeClaims',
        kind: 'NodeClaim',
        plural: 'nodeclaims',
        apiBase: '/apis/karpenter.sh/v1',
        group: 'karpenter.sh',
        namespaced: false,
        columns: [
          { label: 'Ready', render: claim => conditionChip(claim) },
          {
            label: 'Instance type',
            render: claim =>
              claim.metadata.labels?.['node.kubernetes.io/instance-type'] ??
              '-',
          },
          { label: 'Node', render: claim => claim.status?.nodeName ?? '-' },
          AGE_COLUMN,
        ],
      },
    ],
  },
];

const ALL_KINDS: KindDef[] = KINDS.flatMap(section => section.items);

/** The version a CRD stores objects in — the one that matters for reads. */
function storedCrdVersion(crd: K8sObject): string {
  const versions = crd.spec?.versions ?? [];
  const stored = versions.find((version: any) => version.storage);
  return stored?.name ?? versions[0]?.name ?? '';
}

// ─── discovered kinds ────────────────────────────────────────────────────────
// Anything the cluster serves that no curated definition covers still gets a
// list and a detail drawer, so a freshly installed operator's CRDs are
// browsable without a code change.

/** Kind-agnostic status, reusing the same heuristic as the status filter. */
const GENERIC_STATUS_COLUMN: Column = {
  label: 'Status',
  render: object => {
    const bucket = resourceStatusBucket(object);
    const meta = STATUS_BUCKETS.find(entry => entry.id === bucket)!;
    return chip(meta.label, meta.color);
  },
  sortValue: object => resourceStatusBucket(object),
};

const GENERIC_COLUMNS: Column[] = [GENERIC_STATUS_COLUMN, AGE_COLUMN];

const GLOBAL_SEARCH_KIND_IDS = new Set([
  'pods',
  'deployments',
  'statefulsets',
  'daemonsets',
  'jobs',
  'cronjobs',
  'services',
  'routes',
  'ingresses',
  'httproutes',
  'persistentvolumeclaims',
  'configmaps',
  'certificates',
  'applications',
]);

interface GlobalSearchResult {
  kind: KindDef;
  object: K8sObject;
}

function resourceSearchText(kind: KindDef, object: K8sObject): string {
  const labels = Object.entries(object.metadata.labels ?? {})
    .map(([key, value]) => `${key}=${value}`)
    .join(' ');
  const owners = (object.metadata.ownerReferences ?? [])
    .map(owner => `${owner.kind}/${owner.name}`)
    .join(' ');
  const images = [
    ...(object.spec?.containers ?? []),
    ...(object.spec?.template?.spec?.containers ?? []),
  ]
    .map((container: any) => container.image ?? '')
    .join(' ');
  return [
    kind.kind,
    kind.label,
    object.metadata.name,
    object.metadata.namespace,
    labels,
    owners,
    images,
    object.status?.phase,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function GlobalSearchDialog({
  api,
  cluster,
  open,
  onClose,
  onSelect,
}: {
  api: KubernetesConsoleApi;
  cluster: string;
  open: boolean;
  onClose: () => void;
  onSelect: (result: GlobalSearchResult) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** At least one kind had more objects than the sweep budget allowed. */
  const [partial, setPartial] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSearched(false);
      setError(null);
      setPartial(false);
    }
  }, [open]);

  const runSearch = useCallback(async () => {
    const term = query.trim().toLowerCase();
    if (!cluster || term.length < 2) return;
    setLoading(true);
    setSearched(true);
    setError(null);
    const kinds = ALL_KINDS.filter(
      kind => kind.namespaced !== false && GLOBAL_SEARCH_KIND_IDS.has(kind.id),
    );
    // Cluster-wide sweep across every searchable kind, so the per-kind page
    // budget stays small. `truncated` records when that budget ran out, so
    // the dialog can say the results are partial instead of implying they
    // are everything.
    let truncated = false;
    const settled = await Promise.allSettled(
      kinds.map(async kind => {
        const list = await api.listAll(
          cluster,
          `${kind.apiBase}/${kind.plural}`,
          { maxPages: 4 },
        );
        if (list.continueToken) truncated = true;
        return list.items
          .filter(object => resourceSearchText(kind, object).includes(term))
          .map(object => ({ kind, object }));
      }),
    );
    const successful = settled.filter(
      (result): result is PromiseFulfilledResult<GlobalSearchResult[]> =>
        result.status === 'fulfilled',
    );
    setResults(
      successful
        .flatMap(result => result.value)
        .sort(
          (a, b) =>
            a.object.metadata.name.localeCompare(b.object.metadata.name) ||
            a.kind.kind.localeCompare(b.kind.kind),
        )
        .slice(0, 200),
    );
    setPartial(truncated);
    if (!successful.length) {
      setError(
        'No searchable resource types were accessible with your permissions.',
      );
    }
    setLoading(false);
  }, [api, cluster, query]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Search Kubernetes resources</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5, mb: 1.5 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={query}
            placeholder="Name, label, owner, image, status, or kind"
            onChange={event => setQuery(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') runSearch();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            disabled={loading || query.trim().length < 2}
            onClick={runSearch}
            sx={{ textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={16} /> : 'Search'}
          </Button>
        </Stack>
        <Typography sx={{ color: COLORS.muted, fontSize: 11.5, mb: 1 }}>
          Searches common namespaced resources across all namespaces you can
          list on {cluster}.
        </Typography>
        {error && <Alert severity="warning">{error}</Alert>}
        {partial && !error && (
          <Alert severity="info" sx={{ mb: 1, py: 0 }}>
            Some resource types have more objects than one search can scan —
            narrow the term or open the resource list to see everything.
          </Alert>
        )}
        <TableContainer sx={{ maxHeight: '55vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Name', 'Kind', 'Namespace', 'Status'].map(label => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map(result => (
                <TableRow
                  hover
                  key={`${result.kind.id}-${result.object.metadata.namespace}-${result.object.metadata.name}`}
                  onClick={() => onSelect(result)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ color: COLORS.blue }}>
                    {result.object.metadata.name}
                  </TableCell>
                  <TableCell>{result.kind.kind}</TableCell>
                  <TableCell>
                    {result.object.metadata.namespace ?? '-'}
                  </TableCell>
                  <TableCell>
                    {result.kind.kind === 'Pod'
                      ? podStatus(result.object).label
                      : result.object.status?.phase ??
                        result.object.status?.health?.status ??
                        '-'}
                  </TableCell>
                </TableRow>
              ))}
              {searched && !loading && !results.length && !error && (
                <TableRow>
                  <TableCell colSpan={4}>
                    No matching resources found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── small presentational pieces ─────────────────────────────────────────────

function StatusChip({ label, color }: { label: string; color: string }) {
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 22,
        border: `1px solid ${color}35`,
        bgcolor: `${color}12`,
        color,
        fontSize: 11,
        fontWeight: 700,
      }}
    />
  );
}

/** Friendly full-panel state for 401/403 — genuine errors keep their alerts. */
function OopsPanel({ detail }: { detail?: string }) {
  return (
    <Box
      sx={{
        py: 8,
        px: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        border: `1px solid ${COLORS.border}`,
        borderRadius: 2,
        bgcolor: COLORS.surface,
      }}
    >
      <LockOutlinedIcon sx={{ fontSize: 46, color: COLORS.amber, mb: 1.5 }} />
      <Typography sx={{ color: COLORS.text, fontSize: 26, fontWeight: 700 }}>
        Oops! You don't have access here.
      </Typography>
      <Typography
        sx={{ color: COLORS.muted, fontSize: 13, mt: 1, maxWidth: 540 }}
      >
        Your Entra ID account is not authorized to view this. If you believe you
        should have access, request it from your platform team.
      </Typography>
      {detail && (
        <Typography
          sx={{
            color: COLORS.muted,
            fontSize: 11.5,
            mt: 1.5,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}
        >
          {detail}
        </Typography>
      )}
    </Box>
  );
}

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '150px 1fr',
        gap: 1,
        py: 0.45,
      }}
    >
      <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
        {label}
      </Typography>
      <Typography
        sx={{ color: COLORS.text, fontSize: 12, wordBreak: 'break-word' }}
      >
        {value ?? '-'}
      </Typography>
    </Box>
  );
}

function LabelChips({ labels }: { labels?: Record<string, string> }) {
  const entries = Object.entries(labels ?? {});
  if (!entries.length) return <>-</>;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {entries.map(([key, value]) => (
        <Chip
          key={key}
          size="small"
          label={`${key}=${value}`}
          sx={{
            height: 20,
            fontSize: 10,
            bgcolor: `${COLORS.purple}12`,
            border: `1px solid ${COLORS.purple}30`,
            color: COLORS.text,
          }}
        />
      ))}
    </Box>
  );
}

function decodeBase64(value: string): string {
  try {
    const bytes = Uint8Array.from(atob(value), ch => ch.charCodeAt(0));
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return `(binary value, ~${Math.floor((value.length * 3) / 4)} bytes)`;
  }
}

/** Secret data with per-key reveal — values decode from base64 on demand. */
function SecretData({ data }: { data: Record<string, string> }) {
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const keys = Object.keys(data);
  if (!keys.length) return null;
  return (
    <>
      <Typography
        sx={{
          color: COLORS.text,
          fontSize: 13,
          fontWeight: 700,
          mt: 2,
          mb: 0.5,
        }}
      >
        Data
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Key', 'Value', ''].map((head, index) => (
                <TableCell
                  key={head || index}
                  sx={{ color: COLORS.muted, fontSize: 11 }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map(key => {
              const shown = Boolean(revealed[key]);
              return (
                <TableRow key={key}>
                  <TableCell sx={{ color: COLORS.text, fontSize: 12 }}>
                    {key}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: shown ? COLORS.text : COLORS.muted,
                      fontSize: 11.5,
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, monospace',
                      wordBreak: 'break-all',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {shown ? decodeBase64(data[key]) : '••••••••'}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 44 }}>
                    <IconButton
                      size="small"
                      aria-label={shown ? 'Hide value' : 'Reveal value'}
                      onClick={() =>
                        setRevealed(state => ({ ...state, [key]: !shown }))
                      }
                      sx={{ color: COLORS.muted }}
                    >
                      {shown ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

// 40 samples at a 15s poll ≈ the last 10 minutes while the drawer is open.
const METRICS_WINDOW = 40;
const METRICS_POLL_MS = 15000;

function UsageChart({
  title,
  color,
  samples,
  request,
  limit,
  format,
  span = METRICS_WINDOW,
}: {
  title: string;
  color: string;
  samples: number[];
  request: number;
  limit: number;
  format: (value: number) => string;
  /** Slots across the x axis; live mode grows into a fixed window. */
  span?: number;
}) {
  const width = 600;
  const height = 110;
  const pad = 6;
  const max = Math.max(...samples, request, limit, Number.EPSILON) * 1.12;
  const x = (index: number) => (index / (Math.max(span, 2) - 1)) * width;
  const y = (value: number) => pad + (height - 2 * pad) * (1 - value / max);
  const points = samples.map(
    (value, index) => `${x(index).toFixed(1)},${y(value).toFixed(1)}`,
  );
  const current = samples[samples.length - 1] ?? 0;

  return (
    <Box sx={{ flex: 1, minWidth: 260 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ color: COLORS.text, fontSize: 12, fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
          {format(current)}
          {request > 0 ? ` · req ${format(request)}` : ''}
          {limit > 0 ? ` · limit ${format(limit)}` : ''}
        </Typography>
      </Box>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{
          width: '100%',
          height: 110,
          display: 'block',
          background: '#161616',
          border: `1px solid ${COLORS.border}`,
          borderRadius: 4,
        }}
      >
        {limit > 0 && (
          <line
            x1={0}
            x2={width}
            y1={y(limit)}
            y2={y(limit)}
            stroke={COLORS.red}
            strokeDasharray="6 4"
            vectorEffect="non-scaling-stroke"
          />
        )}
        {request > 0 && (
          <line
            x1={0}
            x2={width}
            y1={y(request)}
            y2={y(request)}
            stroke={COLORS.amber}
            strokeDasharray="6 4"
            vectorEffect="non-scaling-stroke"
          />
        )}
        {points.length >= 2 && (
          <>
            <polygon
              points={`${x(0).toFixed(1)},${height - pad} ${points.join(
                ' ',
              )} ${x(samples.length - 1).toFixed(1)},${height - pad}`}
              fill={color}
              opacity={0.18}
            />
            <polyline
              points={points.join(' ')}
              fill="none"
              stroke={color}
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
            />
          </>
        )}
        {points.length === 1 && (
          <circle cx={x(0)} cy={y(current)} r={3} fill={color} />
        )}
      </svg>
    </Box>
  );
}

const METRICS_RANGES: { label: string; minutes: number }[] = [
  { label: 'Live', minutes: 0 },
  { label: '1h', minutes: 60 },
  { label: '6h', minutes: 360 },
  { label: '24h', minutes: 1440 },
  { label: '7d', minutes: 10080 },
];

/**
 * CPU/memory usage for a pod. Historical ranges come from the Thanos store
 * via the backend; when no store is configured (or the query fails) the
 * charts fall back to live polling of the metrics API. Requests (amber) and
 * limits (red), summed across containers, are drawn as dashed reference
 * lines.
 */
function PodMetricsCharts({
  cluster,
  namespace,
  name,
  pod,
}: {
  cluster: string;
  namespace: string;
  name: string;
  pod: K8sObject;
}) {
  const api = useKubernetesConsoleApi();
  const [samples, setSamples] = useState<{ cpu: number; memory: number }[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  // Minutes of history; 0 = live polling. History is the default and drops
  // to live automatically on the first failed store query.
  const [range, setRange] = useState(60);
  const [historyAvailable, setHistoryAvailable] = useState<boolean | null>(
    null,
  );

  useEffect(() => {
    if (range === 0) return undefined;
    let cancelled = false;
    const fetchRange = async () => {
      try {
        const result = await api.podMetricsRange(
          cluster,
          namespace,
          name,
          range,
        );
        if (cancelled) return;
        const memoryByTime = new Map(
          result.memory.map(([time, value]) => [time, Number(value)]),
        );
        setSamples(
          result.cpu.length
            ? result.cpu.map(([time, value]) => ({
                cpu: Number(value),
                memory: memoryByTime.get(time) ?? 0,
              }))
            : result.memory.map(([, value]) => ({
                cpu: 0,
                memory: Number(value),
              })),
        );
        setHistoryAvailable(true);
      } catch {
        if (!cancelled) {
          setHistoryAvailable(false);
          setRange(0);
        }
      }
    };
    setSamples([]);
    fetchRange();
    const timer = setInterval(fetchRange, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api, cluster, namespace, name, range]);

  useEffect(() => {
    if (range !== 0) return undefined;
    let cancelled = false;
    let failed = false;
    setSamples([]);
    setUnavailable(false);
    const poll = async () => {
      if (failed) return;
      try {
        const metrics = await api.proxy<any>(
          cluster,
          `/apis/metrics.k8s.io/v1beta1/namespaces/${encodeURIComponent(
            namespace,
          )}/pods/${encodeURIComponent(name)}`,
        );
        if (cancelled) return;
        const containers: any[] = metrics.containers ?? [];
        setSamples(previous =>
          [
            ...previous,
            {
              cpu: containers.reduce(
                (sum, c) => sum + parseCpu(c.usage?.cpu),
                0,
              ),
              memory: containers.reduce(
                (sum, c) => sum + parseMemory(c.usage?.memory),
                0,
              ),
            },
          ].slice(-METRICS_WINDOW),
        );
      } catch {
        failed = true;
        if (!cancelled) setUnavailable(true);
      }
    };
    poll();
    const timer = setInterval(poll, METRICS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api, cluster, namespace, name, range]);

  const totals = (pod.spec?.containers ?? []).reduce(
    (sum: Record<string, number>, container: any) => ({
      cpuRequest: sum.cpuRequest + parseCpu(container.resources?.requests?.cpu),
      cpuLimit: sum.cpuLimit + parseCpu(container.resources?.limits?.cpu),
      memoryRequest:
        sum.memoryRequest + parseMemory(container.resources?.requests?.memory),
      memoryLimit:
        sum.memoryLimit + parseMemory(container.resources?.limits?.memory),
    }),
    { cpuRequest: 0, cpuLimit: 0, memoryRequest: 0, memoryLimit: 0 },
  );

  const span = range === 0 ? METRICS_WINDOW : samples.length;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 2,
          mb: 0.5,
        }}
      >
        <Typography sx={{ color: COLORS.text, fontSize: 13, fontWeight: 700 }}>
          Usage
        </Typography>
        {historyAvailable !== false && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {METRICS_RANGES.map(option => (
              <Chip
                key={option.label}
                label={option.label}
                size="small"
                onClick={() => setRange(option.minutes)}
                sx={{
                  height: 20,
                  fontSize: 10.5,
                  cursor: 'pointer',
                  color: range === option.minutes ? COLORS.text : COLORS.muted,
                  backgroundColor:
                    range === option.minutes ? '#333333' : 'transparent',
                  border: `1px solid ${COLORS.border}`,
                }}
              />
            ))}
          </Box>
        )}
      </Box>
      {unavailable ? (
        <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
          Usage metrics are not available on this cluster.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <UsageChart
            title="CPU"
            color={COLORS.blue}
            samples={samples.map(sample => sample.cpu)}
            request={totals.cpuRequest}
            limit={totals.cpuLimit}
            format={formatCpu}
            span={span}
          />
          <UsageChart
            title="Memory"
            color={COLORS.purple}
            samples={samples.map(sample => sample.memory)}
            request={totals.memoryRequest}
            limit={totals.memoryLimit}
            format={formatMemory}
            span={span}
          />
        </Box>
      )}
    </>
  );
}

export function EventsTable({ events }: { events: K8sEvent[] }) {
  if (!events.length) {
    return (
      <Typography sx={{ color: COLORS.muted, fontSize: 12, py: 2 }}>
        No events found.
      </Typography>
    );
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Type', 'Reason', 'Message', 'Count', 'Last seen'].map(head => (
              <TableCell key={head} sx={{ color: COLORS.muted, fontSize: 11 }}>
                {head}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event, index) => (
            <TableRow key={event.metadata?.uid ?? index}>
              <TableCell>
                <StatusChip
                  label={event.type ?? 'Normal'}
                  color={event.type === 'Warning' ? COLORS.amber : COLORS.blue}
                />
              </TableCell>
              <TableCell sx={{ color: COLORS.text, fontSize: 12 }}>
                {event.reason}
              </TableCell>
              <TableCell sx={{ color: COLORS.text, fontSize: 12 }}>
                {event.message}
              </TableCell>
              <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                {event.count ?? 1}
              </TableCell>
              <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                {ago(event.lastTimestamp ?? event.eventTime)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CodeBlock({ text }: { text: string }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        bgcolor: '#141414',
        border: `1px solid ${COLORS.border}`,
        borderRadius: 1,
        color: COLORS.text,
        fontSize: 11.5,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        overflow: 'auto',
        maxHeight: '62vh',
        whiteSpace: 'pre',
      }}
    >
      {text}
    </Box>
  );
}

function ScaleRing({
  ready,
  desired,
  busy,
  canPatch,
  onScale,
}: {
  ready: number;
  desired: number;
  busy: boolean;
  canPatch: boolean;
  onScale: (replicas: number) => void;
}) {
  const size = 130;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const fraction = desired > 0 ? Math.min(ready / desired, 1) : 0;
  const settled = ready === desired;

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={COLORS.border}
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={desired === 0 ? COLORS.muted : COLORS.blue}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${fraction * circumference} ${circumference}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dasharray 300ms ease' }}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              color: COLORS.text,
              fontSize: 30,
              fontWeight: 700,
              lineHeight: 1.1,
            }}
          >
            {ready}
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
            {ready === 1 ? 'Pod' : 'Pods'}
          </Typography>
          {!settled && (
            <Typography sx={{ color: COLORS.amber, fontSize: 10.5 }}>
              scaling to {desired}
            </Typography>
          )}
        </Box>
      </Box>
      <Tooltip
        title={canPatch ? '' : 'Your account lacks patch permission here'}
      >
        <Stack spacing={0.75}>
          <IconButton
            size="small"
            aria-label="Increase pod count"
            disabled={!canPatch || busy}
            onClick={() => onScale(desired + 1)}
            sx={{
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 1,
            }}
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Decrease pod count"
            disabled={!canPatch || busy || desired === 0}
            onClick={() => onScale(desired - 1)}
            sx={{
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 1,
            }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Tooltip>
    </Stack>
  );
}

function UsageRing({ usedBytes, capacityBytes }: PvcUsageStats) {
  const size = 130;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const fraction =
    capacityBytes > 0 ? Math.min(usedBytes / capacityBytes, 1) : 0;
  const percent = Math.round(fraction * 100);
  const color =
    percent >= 90 ? COLORS.red : percent >= 75 ? COLORS.amber : COLORS.blue;

  return (
    <Stack alignItems="center" spacing={0.5}>
      <Box sx={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={COLORS.border}
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${fraction * circumference} ${circumference}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dasharray 300ms ease' }}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              color: COLORS.text,
              fontSize: 26,
              fontWeight: 700,
              lineHeight: 1.1,
            }}
          >
            {percent}%
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
            used
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
        {formatBytes(usedBytes)} of {formatBytes(capacityBytes)}
      </Typography>
    </Stack>
  );
}

// ─── detail drawer ───────────────────────────────────────────────────────────

interface Selection {
  kind: KindDef;
  object: K8sObject;
}

function DetailDrawer({
  cluster,
  namespace,
  selection,
  tab,
  onTabChange,
  onClose,
  onChanged,
  notify,
  onNavigateToNode,
  onNavigateToPod,
  favorite,
  onToggleFavorite,
}: {
  cluster: string;
  namespace: string;
  selection: Selection;
  /** Owned by the page so the open tab is part of the URL. */
  tab: string;
  onTabChange: (tab: string) => void;
  onClose: () => void;
  onChanged: () => void;
  notify: (message: string) => void;
  onNavigateToNode?: (name: string) => void;
  onNavigateToPod?: (namespace: string, name: string) => void;
  favorite: boolean;
  onToggleFavorite: () => void;
}) {
  const api = useKubernetesConsoleApi();
  const { kind } = selection;
  const name = selection.object.metadata.name;

  const setTab = onTabChange;
  const [live, setLive] = useState<K8sObject | null>(null);
  const [logs, setLogs] = useState('');
  const [logContainer, setLogContainer] = useState('');
  const [logFilter, setLogFilter] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFollowing, setLogsFollowing] = useState(false);
  const logAbortRef = useRef<AbortController | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canPatch, setCanPatch] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canExec, setCanExec] = useState(false);
  const [confirm, setConfirm] = useState<'restart' | 'delete' | null>(null);
  const [busy, setBusy] = useState(false);
  const [yamlEditing, setYamlEditing] = useState(false);
  const [yamlDraft, setYamlDraft] = useState('');
  const [yamlSaving, setYamlSaving] = useState(false);
  const [envEditing, setEnvEditing] = useState(false);
  const [envDraft, setEnvDraft] = useState<Record<string, EnvVar[]>>({});
  const [envSaving, setEnvSaving] = useState(false);

  const namespaced = kind.namespaced !== false;
  const objectPath = namespaced
    ? `${kind.apiBase}/namespaces/${encodeURIComponent(namespace)}/${
        kind.plural
      }/${encodeURIComponent(name)}`
    : `${kind.apiBase}/${kind.plural}/${encodeURIComponent(name)}`;

  const isPod = kind.kind === 'Pod';
  const isService = kind.kind === 'Service';
  const isNode = kind.kind === 'Node';
  const isPvc = kind.kind === 'PersistentVolumeClaim';
  const restartable = ['Deployment', 'StatefulSet', 'DaemonSet'].includes(
    kind.kind,
  );
  const scalable = ['Deployment', 'StatefulSet'].includes(kind.kind);
  // Environment variables can be viewed for any pod, but only edited on the
  // workload kinds that own a pod template (Deployment/StatefulSet).
  const envEditable = scalable;
  const hasEnvTab = isPod || envEditable;

  // Which tabs this kind actually offers. A link can name any of them, and
  // switching kinds keeps the tab only when the new kind has it — so
  // ?tab=logs on a ConfigMap opens Details rather than a blank panel.
  const availableTabs = useMemo(() => {
    const tabs = ['details', 'yaml'];
    if (isPod) tabs.push('logs', 'terminal');
    if (isService || isNode) tabs.push('pods');
    if (hasEnvTab) tabs.push('environment');
    tabs.push('events');
    return tabs;
  }, [isPod, isService, isNode, hasEnvTab]);

  useEffect(() => {
    if (!availableTabs.includes(tab)) onTabChange('details');
  }, [availableTabs, tab, onTabChange]);

  // undefined = loading, null = unavailable
  const [pvcStats, setPvcStats] = useState<PvcUsageStats | null | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!isPvc) return undefined;
    let cancelled = false;
    fetchPvcUsageMap(api, cluster, namespace)
      .then(map => {
        if (!cancelled) setPvcStats(map[name] ?? null);
      })
      .catch(() => {
        if (!cancelled) setPvcStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [api, cluster, namespace, name, isPvc]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const object = await api.proxy<K8sObject>(cluster, objectPath);
      setLive(object);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [api, cluster, objectPath]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        if (restartable || scalable) {
          const result = await api.canI(cluster, {
            group: 'apps',
            resource: kind.plural,
            verb: 'patch',
            namespace,
            name,
          });
          if (!cancelled) setCanPatch(result.allowed);
        }
        if (isPod) {
          const result = await api.canI(cluster, {
            resource: 'pods',
            subresource: 'exec',
            verb: 'create',
            namespace,
            name,
          });
          if (!cancelled) setCanExec(result.allowed);
        }
        const scope = namespaced ? { namespace } : {};
        const [update, remove] = await Promise.all([
          api.canI(cluster, {
            group: kind.group,
            resource: kind.plural,
            verb: 'update',
            name,
            ...scope,
          }),
          api.canI(cluster, {
            group: kind.group,
            resource: kind.plural,
            verb: 'delete',
            name,
            ...scope,
          }),
        ]);
        if (!cancelled) {
          setCanEdit(update.allowed);
          setCanDelete(remove.allowed);
        }
      } catch {
        /* leave actions disabled */
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [
    api,
    cluster,
    namespace,
    namespaced,
    name,
    kind.plural,
    kind.group,
    restartable,
    scalable,
    isPod,
  ]);

  const containers: string[] = useMemo(() => {
    const spec = (live ?? selection.object).spec;
    return [
      ...(spec?.initContainers ?? []).map((c: any) => c.name),
      ...(spec?.containers ?? []).map((c: any) => c.name),
    ];
  }, [live, selection.object]);

  useEffect(() => {
    if (isPod && containers.length && !logContainer) {
      setLogContainer(
        containers[containers.length ? containers.length - 1 : 0],
      );
    }
  }, [isPod, containers, logContainer]);

  const loadLogs = useCallback(async () => {
    if (!isPod || !logContainer) return;
    setLogsLoading(true);
    try {
      const text = await api.proxyText(
        cluster,
        `/api/v1/namespaces/${encodeURIComponent(
          namespace,
        )}/pods/${encodeURIComponent(name)}/log?container=${encodeURIComponent(
          logContainer,
        )}&tailLines=300&timestamps=true`,
      );
      setLogs(text || '(no log output)');
    } catch (e) {
      setLogs(`Failed to load logs: ${e instanceof Error ? e.message : e}`);
    } finally {
      setLogsLoading(false);
    }
  }, [api, cluster, namespace, name, logContainer, isPod]);

  useEffect(() => {
    if (tab === 'logs') loadLogs();
  }, [tab, loadLogs]);

  const stopFollowingLogs = useCallback(() => {
    logAbortRef.current?.abort();
    logAbortRef.current = null;
    setLogsFollowing(false);
  }, []);

  const followLogs = useCallback(async () => {
    if (!isPod || !logContainer) return;
    stopFollowingLogs();
    const controller = new AbortController();
    logAbortRef.current = controller;
    setLogs('');
    setLogsFollowing(true);
    try {
      await api.followPodLogs(
        cluster,
        namespace,
        name,
        logContainer,
        chunk =>
          setLogs(current => {
            const next = current + chunk;
            return next.length > 1_000_000 ? next.slice(-1_000_000) : next;
          }),
        controller.signal,
      );
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        setLogs(
          current =>
            `${current}${current ? '\n' : ''}Log stream ended: ${
              e instanceof Error ? e.message : String(e)
            }`,
        );
      }
    } finally {
      if (logAbortRef.current === controller) {
        logAbortRef.current = null;
        setLogsFollowing(false);
      }
    }
  }, [api, cluster, isPod, logContainer, name, namespace, stopFollowingLogs]);

  useEffect(
    () => () => {
      logAbortRef.current?.abort();
    },
    [],
  );

  useEffect(() => {
    stopFollowingLogs();
  }, [logContainer, stopFollowingLogs, tab]);

  // Pods behind a Service: match the service's label selector, the same
  // mapping the endpoints controller uses. null = loading.
  const [servicePods, setServicePods] = useState<K8sObject[] | null>(null);
  const serviceSelector = isService
    ? ((live ?? selection.object).spec?.selector as
        | Record<string, string>
        | undefined)
    : undefined;

  useEffect(() => {
    if (!isService || tab !== 'pods') return undefined;
    setServicePods(null);
    if (!serviceSelector || !Object.keys(serviceSelector).length) {
      setServicePods([]);
      return undefined;
    }
    let cancelled = false;
    const labelSelector = Object.entries(serviceSelector)
      .map(([key, value]) => `${key}=${value}`)
      .join(',');
    api
      .listAll(
        cluster,
        `/api/v1/namespaces/${encodeURIComponent(
          namespace,
        )}/pods?labelSelector=${encodeURIComponent(labelSelector)}`,
      )
      .then(list => {
        if (!cancelled) setServicePods(list.items);
      })
      .catch(() => {
        if (!cancelled) setServicePods([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, cluster, namespace, name, tab, isService, serviceSelector]);

  // Pods scheduled on a Node: cluster-wide, filtered by spec.nodeName. null =
  // loading.
  const [nodePods, setNodePods] = useState<K8sObject[] | null>(null);

  useEffect(() => {
    if (!isNode || tab !== 'pods') return undefined;
    setNodePods(null);
    let cancelled = false;
    api
      .listAll(
        cluster,
        `/api/v1/pods?fieldSelector=${encodeURIComponent(
          `spec.nodeName=${name}`,
        )}`,
      )
      .then(list => {
        if (!cancelled) setNodePods(list.items);
      })
      .catch(() => {
        if (!cancelled) setNodePods([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, cluster, name, tab, isNode]);

  // Live CPU/memory usage for the pods listed above, from the metrics-server
  // API. Keyed by "namespace/name" so the node tab (which spans namespaces)
  // and the service tab (single namespace) can share one shape. Best effort —
  // absent when no metrics-server is installed.
  const [podMetrics, setPodMetrics] = useState<
    Record<string, { cpu: number; memory: number }>
  >({});

  useEffect(() => {
    if (tab !== 'pods' || !(isService || isNode)) return undefined;
    let cancelled = false;
    const path = isNode
      ? '/apis/metrics.k8s.io/v1beta1/pods'
      : `/apis/metrics.k8s.io/v1beta1/namespaces/${encodeURIComponent(
          namespace,
        )}/pods`;
    api
      .proxy<{ items: any[] }>(cluster, path)
      .then(list => {
        if (!cancelled) setPodMetrics(buildPodMetricsMap(list.items ?? []));
      })
      .catch(() => {
        if (!cancelled) setPodMetrics({});
      });
    return () => {
      cancelled = true;
    };
  }, [api, cluster, namespace, tab, isService, isNode]);

  /** Restart count across all containers in the pod. */
  function podRestarts(pod: K8sObject): number {
    return (pod.status?.containerStatuses ?? []).reduce(
      (sum: number, c: any) => sum + (c.restartCount ?? 0),
      0,
    );
  }

  // Interactive sorting shared by the node and service "Pods" tabs. Numeric
  // fields (CPU/Memory/Restarts/Ready) default to descending on first click
  // so the heaviest consumers surface immediately.
  const [podSortBy, setPodSortBy] = useState<PodSortField>('cpu');
  const [podSortDir, setPodSortDir] = useState<'asc' | 'desc'>('desc');
  const togglePodSort = (field: PodSortField) => {
    setPodSortDir(dir =>
      podSortBy === field
        ? dir === 'asc'
          ? 'desc'
          : 'asc'
        : NUMERIC_POD_SORT_FIELDS.has(field)
        ? 'desc'
        : 'asc',
    );
    setPodSortBy(field);
  };

  // Node pods, with usage attached (unsorted — sorting is applied below by
  // the user-selected column).
  const nodePodsWithUsage = useMemo(() => {
    if (!nodePods) return null;
    return nodePods.map(pod => ({
      pod,
      usage: podMetrics[`${pod.metadata.namespace}/${pod.metadata.name}`] ?? {
        cpu: 0,
        memory: 0,
      },
    }));
  }, [nodePods, podMetrics]);

  // Highest CPU consumer on the node, regardless of the user's chosen sort —
  // used for the "Top" badge.
  const topNodePodKey = useMemo(() => {
    if (!nodePodsWithUsage || !nodePodsWithUsage.length) return null;
    const top = [...nodePodsWithUsage].sort(
      (a, b) => b.usage.cpu - a.usage.cpu || b.usage.memory - a.usage.memory,
    )[0];
    return top.usage.cpu
      ? `${top.pod.metadata.namespace}/${top.pod.metadata.name}`
      : null;
  }, [nodePodsWithUsage]);

  const nodePodsRanked = useMemo(() => {
    if (!nodePodsWithUsage) return null;
    return sortPodsWithUsage(nodePodsWithUsage, podSortBy, podSortDir);
  }, [nodePodsWithUsage, podSortBy, podSortDir]);

  const servicePodsRanked = useMemo(() => {
    if (!servicePods) return null;
    const withUsage = servicePods.map(pod => ({
      pod,
      usage: podMetrics[`${pod.metadata.namespace}/${pod.metadata.name}`] ?? {
        cpu: 0,
        memory: 0,
      },
    }));
    return sortPodsWithUsage(withUsage, podSortBy, podSortDir);
  }, [servicePods, podMetrics, podSortBy, podSortDir]);

  // Client-side log search: show only the lines containing the term.
  const logView = useMemo(() => {
    const term = logFilter.trim().toLowerCase();
    if (!term) return { text: logs, matched: 0, total: 0 };
    const lines = logs.split('\n');
    const matching = lines.filter(line => line.toLowerCase().includes(term));
    return {
      text: matching.join('\n') || '(no matching lines)',
      matched: matching.length,
      total: lines.length,
    };
  }, [logs, logFilter]);

  async function runAction(action: 'restart' | 'delete') {
    setBusy(true);
    try {
      if (action === 'restart') {
        await api.restart(cluster, namespace, kind.plural, name);
        notify(`Rollout restart requested for ${kind.kind}/${name}`);
      } else {
        await api.deleteObject(
          cluster,
          namespaced ? namespace : '',
          kind.plural,
          name,
          kind.apiBase,
        );
        notify(`${kind.kind} ${name} deleted`);
        onChanged();
        onClose();
        return;
      }
      onChanged();
      load();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  async function saveYaml() {
    setYamlSaving(true);
    try {
      await api.updateYaml(
        cluster,
        namespaced ? namespace : '',
        kind.plural,
        name,
        kind.apiBase,
        yamlDraft,
      );
      notify(`${kind.kind}/${name} updated`);
      setYamlEditing(false);
      onChanged();
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e));
    } finally {
      setYamlSaving(false);
    }
  }

  function startEnvEditing() {
    const draft: Record<string, EnvVar[]> = {};
    for (const container of envContainers(live ?? selection.object)) {
      draft[container.name] = container.env.map(envVar => ({ ...envVar }));
    }
    setEnvDraft(draft);
    setEnvEditing(true);
  }

  async function saveEnv() {
    if (!live) return;
    setEnvSaving(true);
    try {
      const updated: K8sObject = JSON.parse(JSON.stringify(live));
      const containers =
        updated.spec?.template?.spec?.containers ??
        updated.spec?.containers ??
        [];
      for (const container of containers) {
        const draft = envDraft[container.name];
        if (!draft) continue;
        container.env = draft.filter(envVar => envVar.name.trim());
      }
      await api.updateYaml(
        cluster,
        namespaced ? namespace : '',
        kind.plural,
        name,
        kind.apiBase,
        toYaml(updated),
      );
      notify(`${kind.kind}/${name} environment updated`);
      setEnvEditing(false);
      onChanged();
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e));
    } finally {
      setEnvSaving(false);
    }
  }

  async function scaleTo(target: number) {
    if (target < 0) return;
    setBusy(true);
    try {
      await api.scale(cluster, namespace, kind.plural, name, target);
      notify(`${kind.kind}/${name} scaled to ${target} replicas`);
      onChanged();
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!scalable || !live) return undefined;
    const desired = live.spec?.replicas ?? 0;
    const readyNow = live.status?.readyReplicas ?? 0;
    if (desired === readyNow) return undefined;
    const timer = setTimeout(load, 4000);
    return () => clearTimeout(timer);
  }, [scalable, live, load]);

  const object = live ?? selection.object;
  const status = isPod ? podStatus(object) : null;

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: 760 },
          top: 'var(--oc-masthead-height, 0px)',
          height: 'calc(100% - var(--oc-masthead-height, 0px))',
          bgcolor: '#181818',
          borderLeft: `1px solid ${COLORS.border}`,
          p: 2.5,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Typography sx={{ color: COLORS.muted, fontSize: 12, fontWeight: 700 }}>
          {kind.kind.toUpperCase()}
        </Typography>
        <Typography
          sx={{
            color: COLORS.text,
            fontSize: 17,
            fontWeight: 700,
            wordBreak: 'break-all',
            flex: 1,
          }}
        >
          {name}
        </Typography>
        {status && <StatusChip label={status.label} color={status.color} />}
        <IconButton size="small" onClick={onClose} sx={{ color: COLORS.muted }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SmartToyIcon />}
          component={RouterLink}
          to={`/nora?${new URLSearchParams({
            cluster,
            ...(namespaced ? { namespace } : {}),
            kind: kind.kind,
            name,
            returnTo: `/kubernetes?${new URLSearchParams({
              cluster,
              ...(namespaced ? { namespace } : {}),
              section: kind.id,
              kind: kind.kind,
              name,
            })}`,
          })}`}
          sx={{ textTransform: 'none' }}
        >
          Ask Nora
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={favorite ? <StarIcon /> : <StarBorderIcon />}
          onClick={onToggleFavorite}
          sx={{ textTransform: 'none' }}
        >
          {favorite ? 'Favorited' : 'Favorite'}
        </Button>
        {restartable && (
          <Tooltip
            title={canPatch ? '' : 'Your account lacks patch permission here'}
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RestartAltIcon />}
                disabled={!canPatch || busy}
                onClick={() => setConfirm('restart')}
                sx={{ textTransform: 'none' }}
              >
                Restart rollout
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={canDelete ? '' : 'Your account lacks delete permission here'}
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              disabled={!canDelete || busy}
              onClick={() => setConfirm('delete')}
              sx={{ textTransform: 'none' }}
            >
              Delete
            </Button>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => {
            load();
            if (tab === 'logs') loadLogs();
          }}
          sx={{ textTransform: 'none', color: COLORS.muted }}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{
          borderBottom: `1px solid ${COLORS.border}`,
          mb: 1.5,
          minHeight: 36,
        }}
      >
        <Tab
          label="Details"
          value="details"
          sx={{ minHeight: 36, textTransform: 'none' }}
        />
        <Tab
          label="YAML"
          value="yaml"
          sx={{ minHeight: 36, textTransform: 'none' }}
        />
        {isPod && (
          <Tab
            label="Logs"
            value="logs"
            sx={{ minHeight: 36, textTransform: 'none' }}
          />
        )}
        {isPod && (
          <Tab
            label="Terminal"
            value="terminal"
            disabled={!canExec}
            title={
              canExec
                ? 'Open an interactive shell'
                : 'Your account lacks pods/exec permission'
            }
            sx={{ minHeight: 36, textTransform: 'none' }}
          />
        )}
        {(isService || isNode) && (
          <Tab
            label="Pods"
            value="pods"
            sx={{ minHeight: 36, textTransform: 'none' }}
          />
        )}
        {hasEnvTab && (
          <Tab
            label="Environment"
            value="environment"
            sx={{ minHeight: 36, textTransform: 'none' }}
          />
        )}
        <Tab
          label="Events"
          value="events"
          sx={{ minHeight: 36, textTransform: 'none' }}
        />
      </Tabs>

      {tab === 'details' && (
        <Box>
          {scalable && (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}
            >
              <ScaleRing
                ready={object.status?.readyReplicas ?? 0}
                desired={object.spec?.replicas ?? 0}
                busy={busy}
                canPatch={canPatch}
                onScale={scaleTo}
              />
            </Box>
          )}
          {isPvc && (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}
            >
              {pvcStats ? (
                <UsageRing
                  usedBytes={pvcStats.usedBytes}
                  capacityBytes={pvcStats.capacityBytes}
                />
              ) : (
                <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                  {pvcStats === undefined
                    ? 'Loading disk usage…'
                    : 'Disk usage unavailable — the claim is not mounted by a running pod, or your account lacks nodes/proxy (kubelet stats) access.'}
                </Typography>
              )}
            </Box>
          )}
          {namespaced && <Field label="Namespace" value={namespace} />}
          <Field
            label="Created"
            value={
              object.metadata.creationTimestamp
                ? `${new Date(
                    object.metadata.creationTimestamp,
                  ).toLocaleString()} (${ago(
                    object.metadata.creationTimestamp,
                  )} ago)`
                : '-'
            }
          />
          <Field
            label="Labels"
            value={<LabelChips labels={object.metadata.labels} />}
          />
          <CatalogOwnership object={object} />
          {object.metadata.ownerReferences?.length ? (
            <Field
              label="Owner"
              value={object.metadata.ownerReferences
                .map(owner => `${owner.kind}/${owner.name}`)
                .join(', ')}
            />
          ) : null}
          {isPod && (
            <>
              <Field
                label="Node"
                value={
                  object.spec?.nodeName ? (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => onNavigateToNode?.(object.spec.nodeName)}
                      sx={{ color: COLORS.blue, fontSize: 'inherit' }}
                    >
                      {object.spec.nodeName}
                    </Link>
                  ) : undefined
                }
              />
              <Field label="Pod IP" value={object.status?.podIP} />
              <Field
                label="Service account"
                value={object.spec?.serviceAccountName}
              />
              <PodMetricsCharts
                cluster={cluster}
                namespace={namespace}
                name={name}
                pod={object}
              />
            </>
          )}
          {kind.kind === 'Secret' && object.data && (
            <SecretData data={object.data} />
          )}
          {(object.spec?.containers ?? []).length > 0 && (
            <>
              <Typography
                sx={{
                  color: COLORS.text,
                  fontSize: 13,
                  fontWeight: 700,
                  mt: 2,
                  mb: 0.5,
                }}
              >
                Containers
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        'Name',
                        'Image',
                        ...(isPod ? ['State', 'Restarts'] : []),
                      ].map(head => (
                        <TableCell
                          key={head}
                          sx={{ color: COLORS.muted, fontSize: 11 }}
                        >
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(
                      object.spec?.containers ??
                      object.spec?.template?.spec?.containers ??
                      []
                    ).map((container: any) => {
                      const containerStatus = (
                        object.status?.containerStatuses ?? []
                      ).find((s: any) => s.name === container.name);
                      const state = containerStatus?.state
                        ? Object.keys(containerStatus.state)[0]
                        : undefined;
                      return (
                        <TableRow key={container.name}>
                          <TableCell sx={{ color: COLORS.text, fontSize: 12 }}>
                            {container.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: COLORS.muted,
                              fontSize: 11.5,
                              wordBreak: 'break-all',
                            }}
                          >
                            {container.image}
                          </TableCell>
                          {isPod && (
                            <>
                              <TableCell>
                                {state ? (
                                  <StatusChip
                                    label={state}
                                    color={
                                      state === 'running'
                                        ? COLORS.green
                                        : state === 'waiting'
                                        ? COLORS.amber
                                        : COLORS.muted
                                    }
                                  />
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              <TableCell
                                sx={{ color: COLORS.muted, fontSize: 12 }}
                              >
                                {containerStatus?.restartCount ?? 0}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {(object.status?.conditions ?? []).length > 0 && (
            <>
              <Typography
                sx={{
                  color: COLORS.text,
                  fontSize: 13,
                  fontWeight: 700,
                  mt: 2,
                  mb: 0.5,
                }}
              >
                Conditions
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Type', 'Status', 'Reason', 'Message'].map(head => (
                        <TableCell
                          key={head}
                          sx={{ color: COLORS.muted, fontSize: 11 }}
                        >
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(object.status?.conditions ?? []).map((condition: any) => (
                      <TableRow key={condition.type}>
                        <TableCell sx={{ color: COLORS.text, fontSize: 12 }}>
                          {condition.type}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            label={condition.status}
                            color={
                              condition.status === 'True'
                                ? COLORS.green
                                : COLORS.amber
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {condition.reason ?? '-'}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {condition.message ?? '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      {tab === 'yaml' &&
        (yamlEditing ? (
          <Box>
            <Box
              sx={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 560,
              }}
            >
              <CodeEditor
                value={yamlDraft}
                language="yaml"
                onChange={event => setYamlDraft(event.target.value)}
                padding={12}
                minHeight={360}
                data-color-mode="dark"
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 12,
                  backgroundColor: COLORS.surface,
                  color: COLORS.text,
                }}
              />
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="contained"
                disabled={yamlSaving || !yamlDraft.trim()}
                startIcon={
                  yamlSaving ? <CircularProgress size={13} /> : undefined
                }
                onClick={saveYaml}
                sx={{ textTransform: 'none' }}
              >
                {yamlSaving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                size="small"
                disabled={yamlSaving}
                onClick={() => setYamlEditing(false)}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Stack direction="row" sx={{ mb: 1 }}>
              <Tooltip
                title={
                  canEdit ? '' : 'Your account lacks update permission here'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditOutlinedIcon />}
                    disabled={!canEdit || !live}
                    onClick={() => {
                      setYamlDraft(editableYaml(live!));
                      setYamlEditing(true);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit YAML
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            <CodeBlock
              text={
                live ? toYaml(redactSecret(live)) : 'Loading live manifest…'
              }
            />
          </Box>
        ))}

      {tab === 'logs' && isPod && (
        <Box>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <TextField
              select
              size="small"
              label="Container"
              value={logContainer}
              onChange={event => setLogContainer(event.target.value)}
              sx={{ minWidth: 220 }}
            >
              {containers.map(container => (
                <MenuItem key={container} value={container}>
                  {container}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder="Search logs"
              value={logFilter}
              onChange={event => setLogFilter(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: COLORS.muted }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 220 }}
            />
            <Button
              size="small"
              startIcon={
                logsLoading ? <CircularProgress size={13} /> : <RefreshIcon />
              }
              disabled={logsLoading}
              onClick={loadLogs}
              sx={{ textTransform: 'none' }}
            >
              Reload
            </Button>
            <Button
              size="small"
              variant={logsFollowing ? 'contained' : 'outlined'}
              color={logsFollowing ? 'error' : 'primary'}
              onClick={logsFollowing ? stopFollowingLogs : followLogs}
              sx={{ textTransform: 'none' }}
            >
              {logsFollowing ? 'Stop follow' : 'Follow'}
            </Button>
            <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
              {logFilter.trim()
                ? `${logView.matched} of ${logView.total} lines match`
                : logsFollowing
                ? 'Following live output'
                : 'Last 300 lines'}
            </Typography>
          </Stack>
          <CodeBlock text={logView.text || 'Loading logs…'} />
        </Box>
      )}

      {tab === 'terminal' && isPod && (
        <Box>
          <TextField
            select
            size="small"
            label="Container"
            value={logContainer}
            onChange={event => setLogContainer(event.target.value)}
            sx={{ minWidth: 220, mb: 1 }}
          >
            {containers.map(container => (
              <MenuItem key={container} value={container}>
                {container}
              </MenuItem>
            ))}
          </TextField>
          <PodTerminal
            key={`${name}-${logContainer}`}
            cluster={cluster}
            namespace={namespace}
            pod={name}
            container={logContainer}
          />
        </Box>
      )}

      {tab === 'pods' && isService && (
        <Box>
          {servicePodsRanked === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={22} sx={{ color: COLORS.blue }} />
            </Box>
          ) : !servicePodsRanked.length ? (
            <Typography sx={{ color: COLORS.muted, fontSize: 12, py: 2 }}>
              {serviceSelector && Object.keys(serviceSelector).length
                ? 'No pods match this service selector.'
                : 'This service has no pod selector (headless or external).'}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {(
                      [
                        ['Name', 'name'],
                        ['Owner', 'owner'],
                        ['Status', 'status'],
                        ['Restarts', 'restarts'],
                        ['CPU', 'cpu'],
                        ['Memory', 'memory'],
                        ['Ready', 'ready'],
                        ['Pod IP', 'podIP'],
                        ['Age', 'age'],
                      ] as [string, PodSortField][]
                    ).map(([head, field]) => (
                      <TableCell
                        key={head}
                        sx={{ color: COLORS.muted, fontSize: 11 }}
                      >
                        <TableSortLabel
                          active={podSortBy === field}
                          direction={podSortBy === field ? podSortDir : 'asc'}
                          onClick={() => togglePodSort(field)}
                        >
                          {head}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {servicePodsRanked.map(({ pod, usage }) => {
                    const statuses = pod.status?.containerStatuses ?? [];
                    const status = podStatus(pod);
                    return (
                      <TableRow key={pod.metadata.name}>
                        <TableCell
                          sx={{
                            color: COLORS.text,
                            fontSize: 12,
                            wordBreak: 'break-all',
                          }}
                        >
                          {pod.metadata.name}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {podOwner(pod)}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            label={status.label}
                            color={status.color}
                          />
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {podRestarts(pod)}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {usage ? formatCpu(usage.cpu) : '-'}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {usage ? formatMemory(usage.memory) : '-'}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {`${statuses.filter((s: any) => s.ready).length}/${
                            (pod.spec?.containers ?? []).length
                          }`}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {pod.status?.podIP ?? '-'}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {ago(pod.metadata.creationTimestamp)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {tab === 'pods' && isNode && (
        <Box>
          {nodePodsRanked === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={22} sx={{ color: COLORS.blue }} />
            </Box>
          ) : !nodePodsRanked.length ? (
            <Typography sx={{ color: COLORS.muted, fontSize: 12, py: 2 }}>
              No pods are scheduled on this node.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {(
                      [
                        ['Name', 'name'],
                        ['Namespace', 'namespace'],
                        ['Owner', 'owner'],
                        ['Status', 'status'],
                        ['Restarts', 'restarts'],
                        ['CPU', 'cpu'],
                        ['Memory', 'memory'],
                        ['Ready', 'ready'],
                        ['Pod IP', 'podIP'],
                        ['Age', 'age'],
                      ] as [string, PodSortField][]
                    ).map(([head, field]) => (
                      <TableCell
                        key={head}
                        sx={{ color: COLORS.muted, fontSize: 11 }}
                      >
                        <TableSortLabel
                          active={podSortBy === field}
                          direction={podSortBy === field ? podSortDir : 'asc'}
                          onClick={() => togglePodSort(field)}
                        >
                          {head}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nodePodsRanked.map(({ pod, usage }) => {
                    const statuses = pod.status?.containerStatuses ?? [];
                    const status = podStatus(pod);
                    const key = `${pod.metadata.namespace}/${pod.metadata.name}`;
                    const isTop = key === topNodePodKey;
                    return (
                      <TableRow
                        key={key}
                        sx={
                          isTop
                            ? {
                                backgroundColor: 'rgba(255, 152, 0, 0.08)',
                              }
                            : undefined
                        }
                      >
                        <TableCell
                          sx={{
                            color: COLORS.text,
                            fontSize: 12,
                            wordBreak: 'break-all',
                          }}
                        >
                          {onNavigateToPod && pod.metadata.namespace ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() =>
                                onNavigateToPod(
                                  pod.metadata.namespace!,
                                  pod.metadata.name,
                                )
                              }
                              sx={{ color: COLORS.blue, fontSize: 'inherit' }}
                            >
                              {pod.metadata.name}
                            </Link>
                          ) : (
                            pod.metadata.name
                          )}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {pod.metadata.namespace}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {podOwner(pod)}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            label={status.label}
                            color={status.color}
                          />
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {podRestarts(pod)}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <span>{formatCpu(usage.cpu)}</span>
                            {isTop && usage.cpu > 0 && (
                              <Tooltip title="Highest CPU usage on this node">
                                <StatusChip label="Top" color={COLORS.amber} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {formatMemory(usage.memory)}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {`${statuses.filter((s: any) => s.ready).length}/${
                            (pod.spec?.containers ?? []).length
                          }`}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {pod.status?.podIP ?? '-'}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                          {ago(pod.metadata.creationTimestamp)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {tab === 'environment' && (
        <Box>
          {envEditable && (
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              {envEditing ? (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={envSaving}
                    startIcon={
                      envSaving ? <CircularProgress size={13} /> : undefined
                    }
                    onClick={saveEnv}
                    sx={{ textTransform: 'none' }}
                  >
                    {envSaving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    size="small"
                    disabled={envSaving}
                    onClick={() => setEnvEditing(false)}
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Tooltip
                  title={
                    canEdit ? '' : 'Your account lacks update permission here'
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditOutlinedIcon />}
                      disabled={!canEdit || !live}
                      onClick={startEnvEditing}
                      sx={{ textTransform: 'none' }}
                    >
                      Edit environment
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          )}
          {envContainers(object).map(container => (
            <Box key={container.name} sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  color: COLORS.text,
                  fontSize: 13,
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                {container.name}
              </Typography>
              {envEditing ? (
                <Box>
                  {(envDraft[container.name] ?? []).map((envVar, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <TextField
                        size="small"
                        label="Name"
                        value={envVar.name}
                        disabled={Boolean(envVar.valueFrom)}
                        onChange={event =>
                          setEnvDraft(draft => ({
                            ...draft,
                            [container.name]: draft[container.name].map(
                              (v, i) =>
                                i === index
                                  ? { ...v, name: event.target.value }
                                  : v,
                            ),
                          }))
                        }
                        sx={{ width: 220 }}
                      />
                      <TextField
                        size="small"
                        label="Value"
                        value={
                          envVar.valueFrom
                            ? envValueFromLabel(envVar.valueFrom)
                            : envVar.value ?? ''
                        }
                        disabled={Boolean(envVar.valueFrom)}
                        onChange={event =>
                          setEnvDraft(draft => ({
                            ...draft,
                            [container.name]: draft[container.name].map(
                              (v, i) =>
                                i === index
                                  ? { ...v, value: event.target.value }
                                  : v,
                            ),
                          }))
                        }
                        fullWidth
                      />
                      <Tooltip
                        title={
                          envVar.valueFrom
                            ? 'Sourced from a ConfigMap/Secret/field — remove it from the YAML tab instead'
                            : 'Remove'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={Boolean(envVar.valueFrom)}
                            onClick={() =>
                              setEnvDraft(draft => ({
                                ...draft,
                                [container.name]: draft[container.name].filter(
                                  (_v, i) => i !== index,
                                ),
                              }))
                            }
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setEnvDraft(draft => ({
                        ...draft,
                        [container.name]: [
                          ...(draft[container.name] ?? []),
                          { name: '', value: '' },
                        ],
                      }))
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    Add variable
                  </Button>
                </Box>
              ) : container.env.length ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Name', 'Value'].map(head => (
                          <TableCell
                            key={head}
                            sx={{ color: COLORS.muted, fontSize: 11 }}
                          >
                            {head}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {container.env.map(envVar => (
                        <TableRow key={envVar.name}>
                          <TableCell sx={{ color: COLORS.text, fontSize: 12 }}>
                            {envVar.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: COLORS.muted,
                              fontSize: 12,
                              wordBreak: 'break-all',
                            }}
                          >
                            {envVar.valueFrom
                              ? envValueFromLabel(envVar.valueFrom)
                              : envVar.value ?? '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                  No environment variables set.
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {tab === 'events' && (
        <EventsPanel
          api={api}
          cluster={cluster}
          namespace={namespace}
          namespaced={namespaced}
          name={name}
        />
      )}

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        <DialogTitle>
          {confirm === 'delete'
            ? `Delete ${kind.kind} ${name}?`
            : `Restart ${name}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm !== 'delete' &&
              'A rollout restart recreates all pods of this workload, the same as kubectl rollout restart.'}
            {confirm === 'delete' &&
              (isPod
                ? 'The pod will be deleted; its controller (if any) will recreate it.'
                : `This permanently deletes ${kind.kind} ${name}${
                    namespaced ? ` from ${namespace}` : ` from ${cluster}`
                  }. Objects managed by GitOps may be recreated by Argo CD.`)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            color={confirm === 'delete' ? 'error' : 'primary'}
            disabled={busy}
            onClick={() =>
              runAction(confirm === 'delete' ? 'delete' : 'restart')
            }
          >
            {busy ? 'Working…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}

// ─── overview panel ──────────────────────────────────────────────────────────

function Metric({
  label,
  value,
  color,
}: {
  label: string;
  value: React.ReactNode;
  color: string;
}) {
  return (
    <Box
      sx={{
        minWidth: 145,
        p: 1.5,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 2,
        bgcolor: COLORS.surface,
      }}
    >
      <Typography sx={{ color: COLORS.muted, fontSize: 11, fontWeight: 700 }}>
        {label.toUpperCase()}
      </Typography>
      <Typography sx={{ mt: 0.25, color, fontSize: 24, fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );
}

function OverviewPanel({
  cluster,
  clusterInfo,
  namespace,
}: {
  cluster: string;
  clusterInfo?: K8sCluster;
  namespace: string;
}) {
  const api = useKubernetesConsoleApi();
  const [pods, setPods] = useState<K8sObject[]>([]);
  const [deployments, setDeployments] = useState<K8sObject[]>([]);
  const [quotas, setQuotas] = useState<K8sObject[]>([]);
  const [events, setEvents] = useState<K8sEvent[]>([]);
  const [alerts, setAlerts] = useState<K8sAlert[]>([]);
  const [pvcUsage, setPvcUsage] = useState<Record<string, PvcUsageStats>>({});
  const [podUsage, setPodUsage] = useState<
    Record<string, { cpu: number; memory: number }>
  >({});
  const [error, setError] = useState<{
    message: string;
    status?: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const base = `/api/v1/namespaces/${encodeURIComponent(namespace)}`;
        // Everything here feeds a count or a health verdict, so a truncated
        // page would not just hide rows — it would report the wrong number.
        // Events stay bounded: the API server returns them unordered, so the
        // "most recent 12" below is only meaningful over a full-ish sweep.
        const [podList, deploymentList, quotaList, eventList] =
          await Promise.all([
            api.listAll(cluster, `${base}/pods`),
            api.listAll(
              cluster,
              `/apis/apps/v1/namespaces/${encodeURIComponent(
                namespace,
              )}/deployments`,
            ),
            api.listAll(cluster, `${base}/resourcequotas`),
            api.listAll(cluster, `${base}/events`, { maxPages: 4 }),
          ]);
        if (cancelled) return;
        setPods(podList.items);
        setDeployments(deploymentList.items);
        setQuotas(quotaList.items);
        setEvents(
          (eventList.items as K8sEvent[])
            .sort((a, b) =>
              (b.lastTimestamp ?? b.eventTime ?? '').localeCompare(
                a.lastTimestamp ?? a.eventTime ?? '',
              ),
            )
            .slice(0, 12),
        );
        const [alertResult, usageResult, podMetricsResult] =
          await Promise.allSettled([
            api.namespaceAlerts(cluster, namespace),
            fetchPvcUsageMap(api, cluster, namespace),
            api.proxy<{ items: any[] }>(
              cluster,
              `/apis/metrics.k8s.io/v1beta1/namespaces/${encodeURIComponent(
                namespace,
              )}/pods`,
            ),
          ]);
        if (cancelled) return;
        setAlerts(
          alertResult.status === 'fulfilled' ? alertResult.value.alerts : [],
        );
        setPvcUsage(
          usageResult.status === 'fulfilled' ? usageResult.value : {},
        );
        setPodUsage(
          podMetricsResult.status === 'fulfilled'
            ? buildPodMetricsMap(podMetricsResult.value.items ?? [])
            : {},
        );
      } catch (e) {
        if (!cancelled) {
          setError({
            message: e instanceof Error ? e.message : String(e),
            status: (e as Error & { status?: number }).status,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [api, cluster, namespace]);

  const running = pods.filter(pod => pod.status?.phase === 'Running').length;
  const pending = pods.filter(pod => pod.status?.phase === 'Pending').length;
  const failed = pods.filter(pod => pod.status?.phase === 'Failed').length;
  const deploymentsReady = deployments.filter(
    d => (d.status?.readyReplicas ?? 0) >= (d.spec?.replicas ?? 0),
  ).length;
  const totalCpu = Object.values(podUsage).reduce(
    (sum, usage) => sum + usage.cpu,
    0,
  );
  const totalMemory = Object.values(podUsage).reduce(
    (sum, usage) => sum + usage.memory,
    0,
  );
  const totalRestarts = pods.reduce((sum, pod) => sum + podRestarts(pod), 0);
  const dashboardCard = {
    p: 1.5,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 2,
    bgcolor: COLORS.surface,
  };
  const problems = useMemo(() => {
    const entries: {
      key: string;
      severity: 'error' | 'warning';
      title: string;
      detail: string;
    }[] = [];

    for (const alert of alerts) {
      const severity =
        alert.labels.severity === 'critical' ? 'error' : 'warning';
      entries.push({
        key: `alert-${alert.labels.alertname}-${alert.labels.pod ?? ''}`,
        severity,
        title: alert.labels.alertname ?? 'Firing alert',
        detail:
          [
            alert.labels.pod && `Pod ${alert.labels.pod}`,
            alert.labels.deployment && `Deployment ${alert.labels.deployment}`,
            alert.labels.severity && `severity ${alert.labels.severity}`,
          ]
            .filter(Boolean)
            .join(' · ') || 'Reported by cluster monitoring',
      });
    }

    for (const pod of pods) {
      const status = podStatus(pod);
      const restarts = podRestarts(pod);
      if (status.color === COLORS.red || pod.status?.phase === 'Failed') {
        entries.push({
          key: `pod-${pod.metadata.name}`,
          severity: 'error',
          title: `Pod ${pod.metadata.name}: ${status.label}`,
          detail: `${restarts} restart${restarts === 1 ? '' : 's'}`,
        });
      } else if (pod.status?.phase === 'Pending' || restarts >= 5) {
        entries.push({
          key: `pod-${pod.metadata.name}`,
          severity: 'warning',
          title: `Pod ${pod.metadata.name}: ${status.label}`,
          detail: `${restarts} restart${restarts === 1 ? '' : 's'}`,
        });
      }
    }

    for (const deployment of deployments) {
      const ready = deployment.status?.readyReplicas ?? 0;
      const desired = deployment.spec?.replicas ?? 0;
      if (ready < desired) {
        entries.push({
          key: `deployment-${deployment.metadata.name}`,
          severity: ready === 0 && desired > 0 ? 'error' : 'warning',
          title: `Deployment ${deployment.metadata.name} is not ready`,
          detail: `${ready}/${desired} replicas available`,
        });
      }
    }

    for (const [name, usage] of Object.entries(pvcUsage)) {
      const percent = usage.capacityBytes
        ? Math.round((usage.usedBytes / usage.capacityBytes) * 100)
        : 0;
      if (percent >= 75) {
        entries.push({
          key: `pvc-${name}`,
          severity: percent >= 90 ? 'error' : 'warning',
          title: `PersistentVolumeClaim ${name} is ${percent}% full`,
          detail: `${formatBytes(usage.usedBytes)} of ${formatBytes(
            usage.capacityBytes,
          )}`,
        });
      }
    }

    for (const quota of quotas) {
      for (const [resource, hardValue] of Object.entries(
        quota.status?.hard ?? {},
      )) {
        const usedValue = quota.status?.used?.[resource];
        const parse =
          resource.includes('cpu') || resource.startsWith('requests.cpu')
            ? parseCpu
            : parseMemory;
        const hard = parse(String(hardValue));
        const used = parse(String(usedValue ?? '0'));
        const percent = hard > 0 ? Math.round((used / hard) * 100) : 0;
        if (percent >= 80) {
          entries.push({
            key: `quota-${quota.metadata.name}-${resource}`,
            severity: percent >= 95 ? 'error' : 'warning',
            title: `Quota ${resource} is ${percent}% used`,
            detail: `${usedValue ?? '0'} of ${String(hardValue)} in ${
              quota.metadata.name
            }`,
          });
        }
      }
    }

    for (const event of events.filter(item => item.type === 'Warning')) {
      entries.push({
        key: `event-${event.metadata.uid ?? event.reason}-${event.message}`,
        severity: 'warning',
        title: event.reason ?? 'Kubernetes warning',
        detail: event.message ?? 'No event details provided',
      });
    }
    return entries.slice(0, 12);
  }, [alerts, deployments, events, pods, pvcUsage, quotas]);

  if (error && (error.status === 401 || error.status === 403)) {
    return <OopsPanel detail={error.message} />;
  }

  return (
    <Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      <Box
        sx={{
          ...dashboardCard,
          mb: 2.5,
          border: `1px solid ${
            problems.some(problem => problem.severity === 'error')
              ? COLORS.red
              : problems.length
              ? COLORS.amber
              : COLORS.border
          }`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography
            sx={{ color: COLORS.text, fontSize: 14, fontWeight: 700 }}
          >
            Status
          </Typography>
          <StatusChip
            label={problems.length ? `${problems.length} issues` : 'Healthy'}
            color={problems.length ? COLORS.amber : COLORS.green}
          />
        </Stack>
        {problems.length ? (
          <Stack spacing={0.75}>
            {problems.map(problem => (
              <Alert
                key={problem.key}
                severity={problem.severity}
                variant="outlined"
                sx={{ py: 0, '& .MuiAlert-message': { width: '100%' } }}
              >
                <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                  {problem.title}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: COLORS.muted }}>
                  {problem.detail}
                </Typography>
              </Alert>
            ))}
          </Stack>
        ) : (
          <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
            No failing workloads, firing alerts, warning events, or storage
            pressure detected.
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={dashboardCard}>
          <Typography
            sx={{ color: COLORS.text, fontSize: 14, fontWeight: 700, mb: 1 }}
          >
            Inventory
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Metric
              label="Pods"
              value={loading ? '…' : pods.length}
              color={COLORS.blue}
            />
            <Metric
              label="Running"
              value={loading ? '…' : running}
              color={COLORS.green}
            />
            <Metric
              label="Pending"
              value={loading ? '…' : pending}
              color={pending ? COLORS.amber : COLORS.muted}
            />
            <Metric
              label="Failed"
              value={loading ? '…' : failed}
              color={failed ? COLORS.red : COLORS.muted}
            />
            <Metric
              label="Deployments ready"
              value={
                loading ? '…' : `${deploymentsReady}/${deployments.length}`
              }
              color={
                deploymentsReady === deployments.length
                  ? COLORS.green
                  : COLORS.amber
              }
            />
            {clusterInfo?.nodes !== undefined && (
              <Metric
                label="Cluster nodes"
                value={`${clusterInfo.nodesReady ?? 0}/${clusterInfo.nodes}`}
                color={
                  clusterInfo.nodesReady === clusterInfo.nodes
                    ? COLORS.green
                    : COLORS.amber
                }
              />
            )}
          </Stack>
        </Box>

        <Box sx={dashboardCard}>
          <Typography
            sx={{ color: COLORS.text, fontSize: 14, fontWeight: 700, mb: 1 }}
          >
            Utilization
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Metric
              label="CPU used"
              value={loading ? '…' : formatCpu(totalCpu)}
              color={COLORS.blue}
            />
            <Metric
              label="Memory used"
              value={loading ? '…' : formatMemory(totalMemory)}
              color={COLORS.purple}
            />
            <Metric
              label="Pod restarts"
              value={loading ? '…' : totalRestarts}
              color={totalRestarts >= 5 ? COLORS.amber : COLORS.green}
            />
            <Metric
              label="PVCs tracked"
              value={loading ? '…' : Object.keys(pvcUsage).length}
              color={COLORS.blue}
            />
          </Stack>
        </Box>
      </Box>

      {quotas.length > 0 && (
        <Box sx={{ ...dashboardCard, mb: 2 }}>
          <Typography
            sx={{ color: COLORS.text, fontSize: 13, fontWeight: 700, mb: 1 }}
          >
            Quotas
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Quota', 'Resource', 'Used', 'Hard'].map(head => (
                    <TableCell
                      key={head}
                      sx={{ color: COLORS.muted, fontSize: 11 }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {quotas.flatMap(quota =>
                  Object.keys(quota.status?.hard ?? {}).map(resource => (
                    <TableRow key={`${quota.metadata.name}-${resource}`}>
                      <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                        {quota.metadata.name}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.text, fontSize: 12 }}>
                        {resource}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.text, fontSize: 12 }}>
                        {quota.status?.used?.[resource] ?? '0'}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.muted, fontSize: 12 }}>
                        {quota.status?.hard?.[resource]}
                      </TableCell>
                    </TableRow>
                  )),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        <Box sx={dashboardCard}>
          <Typography
            sx={{ color: COLORS.text, fontSize: 13, fontWeight: 700, mb: 1 }}
          >
            Alerts
          </Typography>
          {alerts.length ? (
            <Stack spacing={0.75}>
              {alerts.slice(0, 12).map((alert, index) => (
                <Alert
                  key={`${alert.labels.alertname}-${alert.labels.pod}-${index}`}
                  severity={
                    alert.labels.severity === 'critical' ? 'error' : 'warning'
                  }
                  variant="outlined"
                  sx={{ py: 0 }}
                >
                  <strong>{alert.labels.alertname ?? 'Firing alert'}</strong>
                  {alert.labels.pod ? ` · ${alert.labels.pod}` : ''}
                </Alert>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
              No firing namespace alerts.
            </Typography>
          )}
        </Box>
        <Box sx={dashboardCard}>
          <Typography
            sx={{ color: COLORS.text, fontSize: 13, fontWeight: 700, mb: 1 }}
          >
            Activity
          </Typography>
          <EventsTable events={events} />
        </Box>
      </Box>
    </Box>
  );
}

// ─── row actions (kebab menu) ─────────────────────────────────────────────────

const RESTARTABLE_KINDS = new Set(['Deployment', 'StatefulSet', 'DaemonSet']);
const SCALABLE_KINDS = new Set(['Deployment', 'StatefulSet']);

/**
 * Per-row kebab menu on the resource list. Reuses the same restart/scale/
 * delete API calls the detail drawer's action buttons use (`api.restart`,
 * `api.scale`, `api.deleteObject`), scoped to this row's object, instead of
 * duplicating that logic.
 */
function RowActionsMenu({
  api,
  cluster,
  namespace,
  kind,
  object,
  favorite,
  onToggleFavorite,
  onOpen,
  onChanged,
  notify,
}: {
  api: KubernetesConsoleApi;
  cluster: string;
  namespace: string;
  kind: KindDef;
  object: K8sObject;
  favorite: boolean;
  onToggleFavorite: () => void;
  onOpen: () => void;
  onChanged: () => void;
  notify: (message: string) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    'restart' | 'delete' | null
  >(null);
  const [scaleOpen, setScaleOpen] = useState(false);
  const [scaleValue, setScaleValue] = useState('');
  const [busy, setBusy] = useState(false);

  const name = object.metadata.name;
  const namespaced = kind.namespaced !== false;
  const restartable = RESTARTABLE_KINDS.has(kind.kind);
  const scalable = SCALABLE_KINDS.has(kind.kind);

  async function doRestart() {
    setBusy(true);
    try {
      await api.restart(cluster, namespace, kind.plural, name);
      notify(`Rollout restart requested for ${kind.kind}/${name}`);
      onChanged();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setConfirmAction(null);
    }
  }

  async function doDelete() {
    setBusy(true);
    try {
      await api.deleteObject(
        cluster,
        namespaced ? namespace : '',
        kind.plural,
        name,
        kind.apiBase,
      );
      notify(`${kind.kind} ${name} deleted`);
      onChanged();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setConfirmAction(null);
    }
  }

  async function doScale() {
    const target = Number(scaleValue);
    if (!Number.isFinite(target) || target < 0) return;
    setBusy(true);
    try {
      await api.scale(cluster, namespace, kind.plural, name, target);
      notify(`${kind.kind}/${name} scaled to ${target} replicas`);
      onChanged();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setScaleOpen(false);
    }
  }

  return (
    <Box onClick={event => event.stopPropagation()}>
      <IconButton
        size="small"
        onClick={event => setAnchorEl(event.currentTarget)}
        sx={{ color: COLORS.muted }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onOpen();
          }}
        >
          Open
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onToggleFavorite();
          }}
        >
          {favorite ? 'Unfavorite' : 'Favorite'}
        </MenuItem>
        {restartable && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setConfirmAction('restart');
            }}
          >
            Restart rollout
          </MenuItem>
        )}
        {scalable && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setScaleValue(String(object.spec?.replicas ?? 0));
              setScaleOpen(true);
            }}
          >
            Scale
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setConfirmAction('delete');
          }}
          sx={{ color: COLORS.red }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={confirmAction !== null}
        onClose={() => !busy && setConfirmAction(null)}
      >
        <DialogTitle>
          {confirmAction === 'delete'
            ? `Delete ${kind.kind} ${name}?`
            : `Restart ${name}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === 'restart' &&
              'A rollout restart recreates all pods of this workload, the same as kubectl rollout restart.'}
            {confirmAction === 'delete' &&
              `This permanently deletes ${kind.kind} ${name}${
                namespaced ? ` from ${namespace}` : ` from ${cluster}`
              }. Objects managed by GitOps may be recreated by Argo CD.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            color={confirmAction === 'delete' ? 'error' : 'primary'}
            disabled={busy}
            onClick={() =>
              confirmAction === 'delete' ? doDelete() : doRestart()
            }
          >
            {busy ? 'Working…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scaleOpen} onClose={() => !busy && setScaleOpen(false)}>
        <DialogTitle>Scale {name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            type="number"
            label="Replicas"
            size="small"
            value={scaleValue}
            onChange={event => setScaleValue(event.target.value)}
            inputProps={{ min: 0 }}
            sx={{ mt: 1, minWidth: 160 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScaleOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" disabled={busy} onClick={doScale}>
            {busy ? 'Working…' : 'Scale'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── label selectors & status filter (list view) ──────────────────────────────

const LABEL_KEY_RE = '[A-Za-z0-9]([-A-Za-z0-9_./]*[A-Za-z0-9])?';
const LABEL_VALUE_RE = '([A-Za-z0-9]([-A-Za-z0-9_.]*[A-Za-z0-9])?)?';

/**
 * Best-effort validation of Kubernetes label-selector syntax (equality,
 * inequality, existence, and set-based `in`/`notin` terms) — good enough to
 * catch typos before they reach the API server, not a full parser.
 */
function validateLabelSelector(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const clauses = trimmed.split(',').map(clause => clause.trim());
  const keyOnly = new RegExp(`^${LABEL_KEY_RE}$`);
  const valueOnly = new RegExp(`^${LABEL_VALUE_RE}$`);
  const setClause = new RegExp(
    `^(${LABEL_KEY_RE})\\s+(in|notin)\\s+\\(([^)]*)\\)$`,
  );
  for (const clause of clauses) {
    if (!clause) return 'Empty selector term';
    if (clause.startsWith('!')) {
      if (!keyOnly.test(clause.slice(1))) return `Invalid selector: ${clause}`;
      continue;
    }
    const setMatch = clause.match(setClause);
    if (setMatch) continue;
    if (/\bin\b|\bnotin\b/.test(clause)) return `Invalid selector: ${clause}`;
    if (clause.includes('!=')) {
      const [key, ...rest] = clause.split('!=');
      if (
        !keyOnly.test(key.trim()) ||
        !valueOnly.test(rest.join('!=').trim())
      ) {
        return `Invalid selector: ${clause}`;
      }
      continue;
    }
    if (clause.includes('=')) {
      const [key, ...rest] = clause.split('=');
      if (!keyOnly.test(key.trim()) || !valueOnly.test(rest.join('=').trim())) {
        return `Invalid selector: ${clause}`;
      }
      continue;
    }
    if (!keyOnly.test(clause)) return `Invalid selector: ${clause}`;
  }
  return null;
}

type StatusBucket = 'healthy' | 'warning' | 'error' | 'unknown';

const STATUS_BUCKETS: { id: StatusBucket; label: string; color: string }[] = [
  { id: 'healthy', label: 'Healthy', color: COLORS.green },
  { id: 'warning', label: 'Warning', color: COLORS.amber },
  { id: 'error', label: 'Error', color: COLORS.red },
  { id: 'unknown', label: 'Unknown', color: COLORS.muted },
];

/**
 * Kind-agnostic status heuristic for the list view's status filter chips.
 * Mirrors the coloring already used by individual kinds' Status/Ready
 * columns (podStatus() for pods, ready-vs-desired replicas for workloads,
 * Ready/Available conditions, and status.phase) so the buckets line up with
 * what the table already shows — applied client-side, never sent to the
 * server.
 */
function resourceStatusBucket(object: K8sObject): StatusBucket {
  const status = (object.status ?? {}) as Record<string, any>;

  if (object.kind === 'Pod' || status.containerStatuses) {
    const { color } = podStatus(object);
    if (color === COLORS.green || color === COLORS.blue) return 'healthy';
    if (color === COLORS.amber) return 'warning';
    if (color === COLORS.red) return 'error';
    return 'unknown';
  }

  if (
    typeof status.readyReplicas === 'number' ||
    typeof object.spec?.replicas === 'number'
  ) {
    const desired = object.spec?.replicas ?? status.replicas ?? 0;
    const ready = status.readyReplicas ?? 0;
    if (desired === 0) return 'unknown';
    if (ready >= desired) return 'healthy';
    if (ready > 0) return 'warning';
    return 'error';
  }

  if (typeof status.desiredNumberScheduled === 'number') {
    const desired = status.desiredNumberScheduled;
    const ready = status.numberReady ?? 0;
    if (desired === 0) return 'unknown';
    if (ready >= desired) return 'healthy';
    if (ready > 0) return 'warning';
    return 'error';
  }

  if (
    status.succeeded !== undefined ||
    status.failed !== undefined ||
    status.active !== undefined
  ) {
    if (status.succeeded) return 'healthy';
    if (status.failed) return 'error';
    if (status.active) return 'warning';
    return 'unknown';
  }

  const conditions = status.conditions as
    | { type?: string; status?: string }[]
    | undefined;
  if (Array.isArray(conditions) && conditions.length) {
    const primary =
      conditions.find(c => c.type === 'Ready') ??
      conditions.find(c => c.type === 'Available');
    const condition = primary ?? conditions[conditions.length - 1];
    if (condition?.status === 'True') return 'healthy';
    if (condition?.status === 'False') return primary ? 'error' : 'unknown';
    return 'unknown';
  }

  if (typeof status.phase === 'string') {
    const phase = status.phase;
    if (
      ['Bound', 'Active', 'Running', 'Succeeded', 'Complete', 'Ready'].includes(
        phase,
      )
    ) {
      return 'healthy';
    }
    if (['Pending', 'Terminating', 'Released'].includes(phase)) {
      return 'warning';
    }
    if (['Failed', 'Lost', 'Expired'].includes(phase)) return 'error';
    return 'unknown';
  }

  return 'unknown';
}

// ─── main page ───────────────────────────────────────────────────────────────

const RECENT_NS_KEY = 'k8sConsole.recentNamespaces';
function favoriteKey(favorite: {
  cluster: string;
  namespace: string;
  section: string;
  name: string;
}): string {
  return [
    favorite.cluster,
    favorite.namespace,
    favorite.section,
    favorite.name,
  ].join('/');
}

/**
 * Resolves a `section`, `resource`, or legacy `kind` parameter onto something
 * the console can actually show. Returns null for anything unrecognised.
 */
function resolveSectionParam(candidate: string): string | null {
  if (STANDALONE_SECTIONS.includes(candidate)) return candidate;
  if (ALL_KINDS.some(kind => kind.id === candidate)) return candidate;
  const byKind = ALL_KINDS.find(
    kind => kind.kind.toLowerCase() === candidate.toLowerCase(),
  );
  if (byKind) return byKind.id;
  // Discovery has not run yet when a link is opened cold, so a discovered
  // kind id is accepted on shape alone and resolved once the catalogue lands.
  return DISCOVERED_KIND_ID_PATTERN.test(candidate) ? candidate : null;
}

export function KubernetesPage() {
  const api = useKubernetesConsoleApi();
  const [urlParams, setUrlParams] = useSearchParams();
  // Read once: from here on the URL is written from state, not read back.
  // Links restore the sender's view; the effect below keeps the address bar
  // matching whatever the reader does next.
  const initialUrlRef = useRef(
    parseConsoleUrl(urlParams, resolveSectionParam),
  );
  const initialUrl = initialUrlRef.current;

  const [clusters, setClusters] = useState<K8sCluster[]>([]);
  const [userEmail, setUserEmail] = useState<string>('');
  const [cluster, setCluster] = useState(initialUrl.cluster);
  const [namespaces, setNamespaces] = useState<string[]>([]);
  const [namespaceListDenied, setNamespaceListDenied] = useState(false);
  const [namespace, setNamespace] = useState(initialUrl.namespace);
  const [section, setSection] = useState(initialUrl.section);
  const [drawerTab, setDrawerTab] = useState(initialUrl.tab);
  const [opsTab, setOpsTab] = useState(initialUrl.ops);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    Workloads: true,
  });
  const [items, setItems] = useState<K8sObject[]>([]);
  const [listError, setListError] = useState<{
    message: string;
    status?: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  // Set when the list sweep stopped at its page cap — carries the token to
  // resume from and the server's estimate of what is still unfetched.
  const [listMore, setListMore] = useState<{
    token: string;
    remaining?: number;
  } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [search, setSearch] = useState(initialUrl.q);
  const [labelSelectorInput, setLabelSelectorInput] = useState(
    initialUrl.labels,
  );
  const [debouncedLabelSelector, setDebouncedLabelSelector] = useState(
    initialUrl.labels,
  );
  const [statusFilter, setStatusFilter] = useState<Set<StatusBucket>>(
    () => new Set(initialUrl.status),
  );
  const [selection, setSelection] = useState<Selection | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [pvcUsage, setPvcUsage] = useState<Record<string, PvcUsageStats>>({});
  const [podMetrics, setPodMetrics] = useState<
    Record<string, { cpu: number; memory: number }>
  >({});
  const [nodeMetrics, setNodeMetrics] = useState<
    Record<string, { cpu: number; memory: number }>
  >({});
  const [nodePoolCpuUsage, setNodePoolCpuUsage] = useState<
    Record<string, number>
  >({});
  const [sortBy, setSortBy] = useState(initialUrl.sort);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialUrl.dir);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [importResults, setImportResults] = useState<ImportResult[] | null>(
    null,
  );
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [noraOpen, setNoraOpen] = useState(false);
  const [favorites, setFavorites] = useState<FavoriteResource[]>([]);
  const [pinnedKinds, setPinnedKindsState] = useState<string[]>([]);
  const deepLinkOpenedRef = useRef('');

  // Preferences (favorites, pinned kinds) are persisted server-side so they
  // follow the signed-in user across browsers/machines. Best effort — the
  // page still works if this fails.
  useEffect(() => {
    let cancelled = false;
    api
      .getPreferences()
      .then(prefs => {
        if (cancelled) return;
        setFavorites(prefs.favorites ?? []);
        setPinnedKindsState(prefs.pinnedKinds ?? []);
      })
      .catch(() => {
        /* preferences are best effort */
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Debounce the label-selector input before it feeds the list fetch and the
  // live watch, so keystrokes don't restart the watch connection.
  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedLabelSelector(labelSelectorInput.trim()),
      400,
    );
    return () => window.clearTimeout(timer);
  }, [labelSelectorInput]);
  const labelSelectorError = useMemo(
    () => validateLabelSelector(debouncedLabelSelector),
    [debouncedLabelSelector],
  );
  const labelSelectorQuery = labelSelectorError ? '' : debouncedLabelSelector;

  // The cluster's full resource catalogue. Curated kinds keep their tuned
  // columns; everything else the cluster serves — every CRD — becomes
  // browsable through a generic list and drawer.
  const catalogue = useApiResources(api, cluster);
  const discoveredKinds = useMemo(
    () => buildDiscoveredKinds(catalogue.resources, ALL_KINDS, GENERIC_COLUMNS),
    [catalogue.resources],
  );
  const allKinds = useMemo(
    () => [...ALL_KINDS, ...discoveredKinds],
    [discoveredKinds],
  );
  const [kindPickerOpen, setKindPickerOpen] = useState(false);

  const activeKind = allKinds.find(kind => kind.id === section);
  // One path serves both the paged REST sweep and the live watch, so the two
  // never disagree about which objects belong in the list.
  const listPath = useMemo(() => {
    if (!activeKind || !cluster) return null;
    if (activeKind.namespaced !== false && !namespace) return null;
    const base =
      activeKind.namespaced === false
        ? `${activeKind.apiBase}/${activeKind.plural}`
        : `${activeKind.apiBase}/namespaces/${encodeURIComponent(namespace)}/${
            activeKind.plural
          }`;
    return labelSelectorQuery
      ? `${base}?labelSelector=${encodeURIComponent(labelSelectorQuery)}`
      : base;
  }, [activeKind, cluster, namespace, labelSelectorQuery]);
  const resourceWatch = useKubernetesWatch(api, cluster, listPath);
  const clusterInfo = clusters.find(c => c.name === cluster);
  // A section is only worth showing if RBAC actually lets this user list the
  // kind behind it — a client-side "perspective" toggle only hid the tab
  // visually while leaving it reachable (and just as forbidden) either way.
  // `undefined` (not yet checked) and `true` (allowed) both keep a kind
  // visible; only a confirmed `false` hides it, so a slow or failed check
  // never hides a tab the user actually has access to.
  const [kindListAccess, setKindListAccess] = useState<
    Record<string, boolean>
  >({});
  useEffect(() => {
    if (!cluster) return;
    let cancelled = false;
    // Namespaced kinds are checked against the selected namespace (RBAC is
    // commonly granted per-namespace); cluster-scoped kinds (Nodes, CRDs
    // like Karpenter's NodePools) are checked without one.
    Promise.all(
      KINDS.flatMap(group => group.items).map(async kind => {
        const key = `${cluster}|${kind.namespaced === false ? '' : namespace}|${kind.id}`;
        try {
          const result = await api.canI(cluster, {
            group: kind.group,
            resource: kind.plural,
            verb: 'list',
            namespace: kind.namespaced === false ? undefined : namespace,
          });
          return [key, result.allowed] as const;
        } catch {
          // Unreachable/erroring check: fail open rather than hide a tab.
          return [key, true] as const;
        }
      }),
    ).then(entries => {
      if (cancelled) return;
      setKindListAccess(current => ({
        ...current,
        ...Object.fromEntries(entries),
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [api, cluster, namespace]);
  const kindIsAccessible = useCallback(
    (kind: KindDef) =>
      kindListAccess[
        `${cluster}|${kind.namespaced === false ? '' : namespace}|${kind.id}`
      ] !== false,
    [cluster, namespace, kindListAccess],
  );
  const visibleKindGroups = useMemo(
    () =>
      KINDS.map(group => ({
        ...group,
        items: group.items.filter(kindIsAccessible),
      })).filter(group => group.items.length),
    [kindIsAccessible],
  );
  const openResource = useCallback((kind: KindDef, object: K8sObject): boolean => {
    setSelection({ kind, object });
    return true;
  }, []);
  const openNoraResource = useCallback(
    async (resource: NoraResourceSelection) => {
      const targetKind = allKinds.find(
        kind => kind.kind.toLowerCase() === resource.kind.toLowerCase(),
      );
      if (!targetKind) {
        setToast(`Unsupported Kubernetes kind: ${resource.kind}`);
        return;
      }
      const targetNamespace =
        targetKind.namespaced === false ? '' : resource.namespace ?? namespace;
      if (targetKind.namespaced !== false && !targetNamespace) {
        setToast(`A namespace is required to open ${targetKind.kind}`);
        return;
      }
      const path =
        targetKind.namespaced === false
          ? `${targetKind.apiBase}/${targetKind.plural}/${encodeURIComponent(
              resource.name,
            )}`
          : `${targetKind.apiBase}/namespaces/${encodeURIComponent(
              targetNamespace,
            )}/${targetKind.plural}/${encodeURIComponent(resource.name)}`;
      try {
        const object = await api.proxy<K8sObject>(resource.cluster, path);
        setCluster(resource.cluster);
        if (targetNamespace) setNamespace(targetNamespace);
        setSection('topology');
        openResource(targetKind, object);
      } catch (error) {
        setToast(error instanceof Error ? error.message : String(error));
      }
    },
    [allKinds, api, namespace, openResource],
  );

  // Clusters (also proves the Entra token round-trip works).
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const response = await api.clusters();
        if (cancelled) return;
        setClusters(response.clusters);
        setUserEmail(response.user.email);
        setGlobalError(null);
        const first =
          response.clusters.find(c => c.connected) ?? response.clusters[0];
        setCluster(current =>
          current && response.clusters.some(item => item.name === current)
            ? current
            : first?.name || '',
        );
      } catch (e) {
        if (!cancelled)
          setGlobalError(e instanceof Error ? e.message : String(e));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Namespaces for the selected cluster; fall back to manual entry when the
  // user's RBAC does not allow listing namespaces cluster-wide.
  useEffect(() => {
    if (!cluster) return;
    let cancelled = false;
    async function load() {
      try {
        // Paged: a namespace missing from the selector because it fell past a
        // limit is indistinguishable from one that does not exist.
        const list = await api.listAll(cluster, '/api/v1/namespaces');
        if (cancelled) return;
        const names = list.items
          .map(ns => ns.metadata.name)
          .sort((a, b) => a.localeCompare(b));
        setNamespaces(names);
        setNamespaceListDenied(false);
        setNamespace(current =>
          current && names.includes(current) ? current : names[0] ?? '',
        );
      } catch (e) {
        if (cancelled) return;
        const status = (e as Error & { status?: number }).status;
        if (status === 403) {
          const recent: string[] = JSON.parse(
            localStorage.getItem(RECENT_NS_KEY) ?? '[]',
          );
          setNamespaces(recent);
          setNamespaceListDenied(true);
          setNamespace(current => current || recent[0] || '');
        } else {
          setListError({
            message: e instanceof Error ? e.message : String(e),
            status,
          });
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [api, cluster]);

  useEffect(() => {
    if (!namespace) return;
    const recent: string[] = JSON.parse(
      localStorage.getItem(RECENT_NS_KEY) ?? '[]',
    );
    localStorage.setItem(
      RECENT_NS_KEY,
      JSON.stringify(
        [namespace, ...recent.filter(ns => ns !== namespace)].slice(0, 10),
      ),
    );
  }, [namespace]);

  // Resource list for the active section. Cluster-scoped kinds (Nodes,
  // ClusterRoles, NodePools, …) list without a namespace in the path.
  useEffect(() => {
    // listPath is null for exactly the cases with nothing to list: no
    // cluster, no kind, or a namespaced kind with no namespace picked.
    if (!cluster || !listPath) {
      setItems([]);
      return;
    }
    const path = listPath;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setListError(null);
      setListMore(null);
      try {
        const page = await api.listAll(cluster, path);
        if (!cancelled) {
          setItems(page.items);
          setListMore(
            page.continueToken
              ? { token: page.continueToken, remaining: page.remaining }
              : null,
          );
        }
      } catch (e) {
        if (!cancelled) {
          setItems([]);
          setListError({
            message: e instanceof Error ? e.message : String(e),
            status: (e as Error & { status?: number }).status,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [api, cluster, listPath, reloadTick]);

  // Fetch the next batch after a sweep stopped at its page cap, appending to
  // what is already on screen rather than restarting the list.
  const loadMore = useCallback(async () => {
    if (!cluster || !listPath || !listMore) return;
    setLoadingMore(true);
    try {
      const page = await api.listAll(cluster, listPath, {
        continueToken: listMore.token,
      });
      setItems(current => [...current, ...page.items]);
      setListMore(
        page.continueToken
          ? { token: page.continueToken, remaining: page.remaining }
          : null,
      );
    } catch (e) {
      setToast(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }, [api, cluster, listPath, listMore]);

  // The watch's initial LIST is unpaged, so once it connects it supersedes
  // whatever the sweep collected — including any truncation it reported.
  useEffect(() => {
    if (!resourceWatch.connected) return;
    setItems(resourceWatch.items);
    setLoading(false);
    setListError(null);
    setListMore(null);
  }, [resourceWatch.connected, resourceWatch.items]);

  // Kubelet volume stats for the PVC list's "Used" column (best effort).
  useEffect(() => {
    if (!cluster || !namespace || section !== 'persistentvolumeclaims') {
      setPvcUsage({});
      return undefined;
    }
    let cancelled = false;
    fetchPvcUsageMap(api, cluster, namespace)
      .then(map => {
        if (!cancelled) setPvcUsage(map);
      })
      .catch(() => {
        if (!cancelled) setPvcUsage({});
      });
    return () => {
      cancelled = true;
    };
  }, [api, cluster, namespace, section, reloadTick]);

  // Live CPU/memory usage for the pods list's CPU/Memory columns (best
  // effort — absent when no metrics-server is installed on the cluster).
  useEffect(() => {
    if (!cluster || !namespace || section !== 'pods') {
      setPodMetrics({});
      return undefined;
    }
    let cancelled = false;
    api
      .proxy<{ items: any[] }>(
        cluster,
        `/apis/metrics.k8s.io/v1beta1/namespaces/${encodeURIComponent(
          namespace,
        )}/pods`,
      )
      .then(list => {
        if (!cancelled) setPodMetrics(buildPodMetricsMap(list.items ?? []));
      })
      .catch(() => {
        if (!cancelled) setPodMetrics({});
      });
    return () => {
      cancelled = true;
    };
  }, [api, cluster, namespace, section, reloadTick]);

  // Live CPU/memory usage for the Nodes list's CPU/Memory columns.
  useEffect(() => {
    if (!cluster || section !== 'nodes') {
      setNodeMetrics({});
      return undefined;
    }
    let cancelled = false;
    api
      .proxy<{ items: any[] }>(cluster, '/apis/metrics.k8s.io/v1beta1/nodes')
      .then(list => {
        if (!cancelled) setNodeMetrics(buildNodeMetricsMap(list.items ?? []));
      })
      .catch(() => {
        if (!cancelled) setNodeMetrics({});
      });
    return () => {
      cancelled = true;
    };
  }, [api, cluster, section, reloadTick]);

  // Nora and shared links can address a resource directly. Once the cluster,
  // namespace, and kind are ready, fetch the live object and open its drawer.
  useEffect(() => {
    const name = urlParams.get('name');
    if (!name || !cluster || !activeKind) return;
    const objectNamespace =
      activeKind.namespaced === false ? '' : namespace;
    if (activeKind.namespaced !== false && !objectNamespace) return;
    const key = `${cluster}/${objectNamespace}/${activeKind.id}/${name}`;
    if (deepLinkOpenedRef.current === key) return;
    // Clicking a row opens the drawer and puts the name in the URL, which
    // lands back here — skip the round trip when the object is already open.
    if (
      selection?.kind.id === activeKind.id &&
      selection.object.metadata.name === name
    ) {
      deepLinkOpenedRef.current = key;
      return;
    }
    deepLinkOpenedRef.current = key;
    const path =
      activeKind.namespaced === false
        ? `${activeKind.apiBase}/${activeKind.plural}/${encodeURIComponent(
            name,
          )}`
        : `${activeKind.apiBase}/namespaces/${encodeURIComponent(
            objectNamespace,
          )}/${activeKind.plural}/${encodeURIComponent(name)}`;
    api
      .proxy<K8sObject>(cluster, path)
      .then(object => {
        if (!openResource(activeKind, object)) {
          deepLinkOpenedRef.current = '';
        }
      })
      .catch(error => {
        deepLinkOpenedRef.current = '';
        setToast(error instanceof Error ? error.message : String(error));
      });
  }, [activeKind, api, cluster, namespace, openResource, selection, urlParams]);

  // Aggregate live CPU usage per Karpenter NodePool (sum of each pool's
  // nodes' current usage) for the NodePools list's "CPU used" column.
  useEffect(() => {
    if (!cluster || section !== 'nodepools') {
      setNodePoolCpuUsage({});
      return undefined;
    }
    let cancelled = false;
    Promise.all([
      api.proxy<{ items: any[] }>(cluster, '/api/v1/nodes'),
      api.proxy<{ items: any[] }>(
        cluster,
        '/apis/metrics.k8s.io/v1beta1/nodes',
      ),
    ])
      .then(([nodeList, metricsList]) => {
        if (cancelled) return;
        const metrics = buildNodeMetricsMap(metricsList.items ?? []);
        const usage: Record<string, number> = {};
        for (const node of nodeList.items ?? []) {
          const pool = node.metadata?.labels?.['karpenter.sh/nodepool'];
          if (!pool) continue;
          usage[pool] =
            (usage[pool] ?? 0) + (metrics[node.metadata.name]?.cpu ?? 0);
        }
        setNodePoolCpuUsage(usage);
      })
      .catch(() => {
        if (!cancelled) setNodePoolCpuUsage({});
      });
    return () => {
      cancelled = true;
    };
  }, [api, cluster, section, reloadTick]);
  // may not exist on the new kind's columns.
  useEffect(() => {
    setSortBy('Name');
    setSortDir('asc');
    setStatusFilter(new Set());
    setLabelSelectorInput('');
  }, [section]);

  // Opens the Service detail drawer from an HTTPRoute's "Services" column —
  // fetches the live Service object so the drawer has real status/spec data.
  const navigateToService = useCallback(
    async (serviceNamespace: string, serviceName: string) => {
      if (!cluster || !serviceNamespace) return;
      const serviceKind = ALL_KINDS.find(k => k.id === 'services');
      if (!serviceKind) return;
      try {
        const service = await api.proxy<K8sObject>(
          cluster,
          `/api/v1/namespaces/${encodeURIComponent(
            serviceNamespace,
          )}/services/${encodeURIComponent(serviceName)}`,
        );
        openResource(serviceKind, service);
      } catch (err) {
        setToast(
          err instanceof Error
            ? err.message
            : `Failed to open service ${serviceName}`,
        );
      }
    },
    [api, cluster, openResource],
  );

  // Opens the Node detail drawer from a Pod's "Node" column — fetches the
  // live Node object so the drawer has real status/capacity data.
  const navigateToNode = useCallback(
    async (nodeName: string) => {
      if (!cluster || !nodeName) return;
      const nodeKind = ALL_KINDS.find(k => k.id === 'nodes');
      if (!nodeKind) return;
      try {
        const node = await api.proxy<K8sObject>(
          cluster,
          `/api/v1/nodes/${encodeURIComponent(nodeName)}`,
        );
        openResource(nodeKind, node);
      } catch (err) {
        setToast(
          err instanceof Error
            ? err.message
            : `Failed to open node ${nodeName}`,
        );
      }
    },
    [api, cluster, openResource],
  );

  // Opens the Pod detail drawer from a Node's "Pods" tab — fetches the live
  // Pod object so the drawer has real status/spec data.
  const navigateToPod = useCallback(
    async (podNamespace: string, podName: string) => {
      if (!cluster || !podNamespace || !podName) return;
      const podKind = ALL_KINDS.find(k => k.id === 'pods');
      if (!podKind) return;
      try {
        const pod = await api.proxy<K8sObject>(
          cluster,
          `/api/v1/namespaces/${encodeURIComponent(
            podNamespace,
          )}/pods/${encodeURIComponent(podName)}`,
        );
        // Pods are namespace-scoped, but this jump can originate from a
        // cluster-scoped view (a Node's Pods tab) where the namespace filter
        // is empty. Sync it to the pod's own namespace — otherwise the
        // drawer's visibility guard (which requires `namespace` truthy for
        // namespaced kinds) never opens it, and any drawer fetches would
        // target the wrong namespace.
        setNamespace(podNamespace);
        openResource(podKind, pod);
      } catch (err) {
        setToast(
          err instanceof Error ? err.message : `Failed to open pod ${podName}`,
        );
      }
    },
    [api, cluster, openResource],
  );

  /** Jump from a CRD row to that CRD's instances. */
  const navigateToKind = useCallback(
    (plural: string, group: string) => {
      const id = discoveredKindId(plural, group);
      const target = allKinds.find(kind => kind.id === id);
      if (!target) {
        setToast(`${plural}.${group} is not served on ${cluster}`);
        return;
      }
      setSection(target.id);
      setSelection(null);
      setDrawerTab('');
      setSearch('');
      setStatusFilter(new Set());
    },
    [allKinds, cluster],
  );

  const columnContext: ColumnContext = {
    pvcUsage,
    podMetrics,
    nodeMetrics,
    nodePoolCpuUsage,
    onNavigateToService: navigateToService,
    onNavigateToNode: navigateToNode,
    onNavigateToKind: navigateToKind,
  };

  const toggleSort = useCallback(
    (label: string) => {
      setSortDir(dir =>
        sortBy === label ? (dir === 'asc' ? 'desc' : 'asc') : 'asc',
      );
      setSortBy(label);
    },
    [sortBy],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const term_filtered = term
      ? items.filter(item => item.metadata.name.toLowerCase().includes(term))
      : items;
    const status_filtered = statusFilter.size
      ? term_filtered.filter(item =>
          statusFilter.has(resourceStatusBucket(item)),
        )
      : term_filtered;
    const column =
      sortBy === 'Name'
        ? undefined
        : activeKind?.columns.find(c => c.label === sortBy && c.sortValue);
    const sorted = [...status_filtered].sort((a, b) => {
      let result: number;
      if (column?.sortValue) {
        const av = column.sortValue(a, columnContext);
        const bv = column.sortValue(b, columnContext);
        result =
          typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv));
      } else {
        result = a.metadata.name.localeCompare(b.metadata.name);
      }
      return sortDir === 'asc' ? result : -result;
    });
    return sorted;
  }, [
    items,
    search,
    statusFilter,
    sortBy,
    sortDir,
    activeKind,
    pvcUsage,
    podMetrics,
    nodeMetrics,
  ]);

  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const rows = useWindowedRows(filtered.length, tableBodyRef);

  // ── URL as the address of the current view ────────────────────────────────
  // Everything the reader can see is derived from this state, so writing it
  // back to the query string is what makes every view linkable.
  const urlState = useMemo<ConsoleUrlState>(
    () => ({
      cluster,
      namespace,
      section,
      name: selection?.object.metadata.name ?? '',
      tab: drawerTab,
      q: search,
      labels: labelSelectorInput,
      status: [...statusFilter],
      sort: sortBy,
      dir: sortDir,
      ops: opsTab,
    }),
    [
      cluster,
      namespace,
      section,
      selection,
      drawerTab,
      search,
      labelSelectorInput,
      statusFilter,
      sortBy,
      sortDir,
      opsTab,
    ],
  );
  const previousUrlStateRef = useRef(urlState);
  const lastWrittenRef = useRef(serializeConsoleUrl(initialUrl).toString());
  /** Set while a URL change from outside is being folded into state. */
  const adoptingRef = useRef(false);

  // Back, Forward, and links pasted into the running app all arrive as a
  // query string this component did not write. Fold them into state, or the
  // write below would immediately undo the reader's navigation.
  useEffect(() => {
    const incoming = urlParams.toString();
    if (incoming === lastWrittenRef.current) return;
    lastWrittenRef.current = incoming;
    adoptingRef.current = true;

    const next = parseConsoleUrl(urlParams, resolveSectionParam);
    setCluster(next.cluster);
    setNamespace(next.namespace);
    setSection(next.section);
    setDrawerTab(next.tab);
    setOpsTab(next.ops);
    setSearch(next.q);
    setLabelSelectorInput(next.labels);
    setStatusFilter(new Set(next.status));
    setSortBy(next.sort);
    setSortDir(next.dir);
    if (!next.name) setSelection(null);
    // Let the deep-link effect re-open an object it has already opened once,
    // so going Back to a resource actually reopens its drawer.
    deepLinkOpenedRef.current = '';
  }, [urlParams]);

  useEffect(() => {
    // The adoption above queued state updates this render has not seen yet.
    // Writing now would serialize the pre-adoption state straight back over
    // the URL the reader just navigated to.
    if (adoptingRef.current) {
      adoptingRef.current = false;
      previousUrlStateRef.current = urlState;
      return;
    }
    const next = serializeConsoleUrl(urlState);
    const serialized = next.toString();
    // Nothing in the address moved — never fight the router or pile up
    // identical history entries.
    if (serialized === urlParams.toString()) {
      previousUrlStateRef.current = urlState;
      return;
    }
    const previous = previousUrlStateRef.current;
    // Filling in a cluster or namespace the URL left blank is the page
    // booting, not the reader navigating; it must not consume a history
    // entry the Back button would then have to step through.
    const settling =
      (!previous.cluster && Boolean(urlState.cluster)) ||
      (!previous.namespace && Boolean(urlState.namespace));
    const navigated = !settling && isNavigationChange(previous, urlState);
    previousUrlStateRef.current = urlState;
    lastWrittenRef.current = serialized;
    // Typing in a filter replaces the entry; moving to another view earns
    // one, so Back steps through views instead of undoing keystrokes.
    setUrlParams(next, { replace: !navigated });
  }, [urlState, urlParams, setUrlParams]);

  /** Absolute link to exactly what is on screen, for the Copy link action. */
  const shareUrl = useMemo(() => {
    const query = serializeConsoleUrl(urlState).toString();
    return `${window.location.origin}${window.location.pathname}${
      query ? `?${query}` : ''
    }`;
  }, [urlState]);

  const copyShareUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToast('Link copied');
    } catch {
      // Clipboard access is denied outside a secure context; showing the URL
      // still lets the user copy it by hand.
      setToast(shareUrl);
    }
  }, [shareUrl]);

  const refresh = useCallback(() => setReloadTick(tick => tick + 1), []);

  const openSearchResult = useCallback(
    (result: GlobalSearchResult) => {
      const resultNamespace = result.object.metadata.namespace ?? '';
      setNamespace(resultNamespace);
      setSection(result.kind.id);
      openResource(result.kind, result.object);
      setGlobalSearchOpen(false);
    },
    [openResource],
  );

  const openFavorite = useCallback(
    async (favorite: FavoriteResource) => {
      const kind = allKinds.find(item => item.id === favorite.section);
      if (!kind) return;
      const path =
        kind.namespaced === false
          ? `${kind.apiBase}/${kind.plural}/${encodeURIComponent(
              favorite.name,
            )}`
          : `${kind.apiBase}/namespaces/${encodeURIComponent(
              favorite.namespace,
            )}/${kind.plural}/${encodeURIComponent(favorite.name)}`;
      try {
        const object = await api.proxy<K8sObject>(favorite.cluster, path);
        setCluster(favorite.cluster);
        setNamespace(favorite.namespace);
        setSection(favorite.section);
        openResource(kind, object);
      } catch (openError) {
        setToast(
          openError instanceof Error ? openError.message : String(openError),
        );
      }
    },
    [allKinds, api, openResource],
  );

  // Favorites are persisted server-side (see api.getPreferences/addFavorite/
  // removeFavorite) so they follow the signed-in user across
  // browsers/machines. Updates optimistically and rolls back on failure.
  const toggleFavorite = useCallback(
    (favorite: {
      cluster: string;
      namespace: string;
      section: string;
      kind: string;
      name: string;
    }) => {
      const key = favoriteKey(favorite);
      const existing = favorites.find(item => favoriteKey(item) === key);
      if (existing) {
        setFavorites(current =>
          current.filter(item => item.key !== existing.key),
        );
        api.removeFavorite(existing.key).catch(e => {
          setFavorites(current => [existing, ...current]);
          setToast(e instanceof Error ? e.message : String(e));
        });
        return;
      }
      const tempKey = `optimistic:${key}`;
      const optimistic: FavoriteResource = {
        ...favorite,
        key: tempKey,
        createdAt: new Date().toISOString(),
      };
      setFavorites(current => [optimistic, ...current]);
      api
        .addFavorite(favorite)
        .then(saved => {
          setFavorites(current =>
            current.map(item => (item.key === tempKey ? saved : item)),
          );
        })
        .catch(e => {
          setFavorites(current => current.filter(item => item.key !== tempKey));
          setToast(e instanceof Error ? e.message : String(e));
        });
    },
    [api, favorites],
  );

  // Pinned kinds (server-side preference) surface at the top of the sidebar
  // nav so they're one click away, mirroring OpenShift Console's "pin to
  // navigation" feature.
  const togglePinnedKind = useCallback(
    (kindId: string) => {
      setPinnedKindsState(current => {
        const previous = current;
        const next = current.includes(kindId)
          ? current.filter(id => id !== kindId)
          : [...current, kindId];
        api.setPinnedKinds(next).catch(e => {
          setPinnedKindsState(previous);
          setToast(e instanceof Error ? e.message : String(e));
        });
        return next;
      });
    },
    [api],
  );

  useEffect(() => {
    const handlePaletteShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', handlePaletteShortcut);
    return () => document.removeEventListener('keydown', handlePaletteShortcut);
  }, []);

  // A direct link or stale bookmark can still address a kind this user's
  // RBAC forbids listing — bounce to the overview instead of leaving a
  // 403-riddled list on screen. Only fires once the check has actually come
  // back `false`; an unchecked (`undefined`) kind is left alone.
  useEffect(() => {
    if (
      activeKind &&
      !activeKind.discovered &&
      KINDS.some(group => group.items.some(item => item.id === activeKind.id)) &&
      !kindIsAccessible(activeKind)
    ) {
      setToast(`You do not have access to ${activeKind.kind} on ${cluster}`);
      setSection('overview');
      setSelection(null);
      setDrawerTab('');
    }
  }, [activeKind, cluster, kindIsAccessible]);

  const commands = useMemo<ConsoleCommand[]>(
    () => [
      {
        id: 'overview',
        label: 'Open namespace overview',
        detail: namespace || 'Select a namespace first',
        keywords: 'status utilization inventory quotas alerts activity',
        run: () => setSection('overview'),
      },
      {
        id: 'topology',
        label: 'Open workload topology',
        detail: namespace || 'Select a namespace first',
        keywords: 'graph deployment pod service route',
        run: () => setSection('topology'),
      },
      {
        id: 'operations',
        label: 'Open operations center',
        detail: 'Incidents, PromQL, GitOps, policy and guided operations',
        keywords:
          'incident metrics promql gitops drift policy rbac quick start',
        run: () => setSection('operations'),
      },
      {
        id: 'search',
        label: 'Search all Kubernetes resources',
        detail: `Across namespaces on ${cluster}`,
        keywords: 'find global labels owners images',
        run: () => setGlobalSearchOpen(true),
      },
      {
        id: 'create',
        label: 'Create or import resource',
        detail: 'Open the YAML import editor',
        keywords: 'add create yaml manifest',
        run: () => {
          setImportText('');
          setImportResults(null);
          setImportOpen(true);
        },
      },
      ...visibleKindGroups.flatMap(group =>
        group.items.map(kind => ({
          id: `kind-${kind.id}`,
          label: `Browse ${kind.label}`,
          detail: group.section,
          keywords: `${kind.kind} ${kind.plural}`,
          run: () => setSection(kind.id),
        })),
      ),
    ],
    [cluster, namespace, visibleKindGroups],
  );

  async function runImport() {
    setImportBusy(true);
    setImportResults(null);
    try {
      const { results } = await api.importYaml(cluster, namespace, importText);
      const created = results.filter(result => result.status < 300).length;
      if (created > 0) refresh();
      if (created === results.length) {
        // All objects created — close and clear; failures keep the dialog
        // open so the YAML can be fixed against the per-object messages.
        setImportOpen(false);
        setImportText('');
        setToast(
          `${created} object${created === 1 ? '' : 's'} created on ${cluster}`,
        );
      } else {
        setImportResults(results);
      }
    } catch (e) {
      setImportResults([
        {
          kind: '',
          name: '',
          status: 0,
          message: e instanceof Error ? e.message : String(e),
        },
      ]);
    } finally {
      setImportBusy(false);
    }
  }

  return (
    <Page themeId="tool">
      <GuidedTour api={api} />
      <Header
        title="Kubernetes"
        subtitle="Native console for the AKS clusters — every request runs with your own Entra ID permissions"
      />
      <Content>
        {globalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {globalError}
          </Alert>
        )}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'center' }}
          sx={{ mb: 2 }}
        >
          <TextField
            select
            size="small"
            label="Cluster"
            data-tour-id="cluster-picker"
            value={cluster}
            onChange={event => {
              setCluster(event.target.value);
              setSelection(null);
            }}
            sx={{ minWidth: 200 }}
          >
            {clusters.map(c => (
              <MenuItem key={c.name} value={c.name}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: c.connected ? COLORS.green : COLORS.red,
                    }}
                  />
                  <span>{c.name}</span>
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          {namespaceListDenied ? (
            <TextField
              size="small"
              label="Project (namespace)"
              data-tour-id="namespace-picker"
              value={namespace}
              onChange={event => setNamespace(event.target.value.trim())}
              helperText="Namespace listing is not permitted for your account — type a namespace you have access to"
              sx={{ minWidth: 260 }}
            />
          ) : (
            <TextField
              select
              size="small"
              label="Project (namespace)"
              data-tour-id="namespace-picker"
              value={namespace}
              onChange={event => {
                setNamespace(event.target.value);
                setSelection(null);
              }}
              sx={{ minWidth: 260 }}
            >
              {namespaces.map(ns => (
                <MenuItem key={ns} value={ns}>
                  {ns}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
              {clusterInfo?.version ? `${clusterInfo.version} · ` : ''}
              Signed in as {userEmail || 'Microsoft Entra user'}
            </Typography>
            {clusterInfo?.error && (
              <Typography sx={{ color: COLORS.amber, fontSize: 12 }}>
                {clusterInfo.error}
              </Typography>
            )}
          </Box>

          <Button
            variant="outlined"
            data-tour-id="command-palette-trigger"
            onClick={() => setCommandPaletteOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Commands ⌘K
          </Button>

          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            disabled={!cluster}
            onClick={() => setGlobalSearchOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Search all
          </Button>

          <Button
            variant={noraOpen ? 'contained' : 'outlined'}
            startIcon={<SmartToyIcon />}
            disabled={!cluster}
            data-tour-id="nora-dock-toggle"
            onClick={() => setNoraOpen(open => !open)}
            sx={{ textTransform: 'none' }}
          >
            {noraOpen ? 'Hide Nora' : 'Ask Nora'}
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!cluster || !namespace}
            onClick={() => {
              setImportText('');
              setImportResults(null);
              setImportOpen(true);
            }}
            sx={{ textTransform: 'none' }}
          >
            Import YAML
          </Button>

          <Button
            variant="outlined"
            startIcon={
              loading ? <CircularProgress size={14} /> : <RefreshIcon />
            }
            disabled={loading}
            onClick={refresh}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>

          <Tooltip title="Copy a link to exactly this view">
            <Button
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={copyShareUrl}
              sx={{ textTransform: 'none' }}
            >
              Copy link
            </Button>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* Section navigation, OpenShift console style */}
          <Box
            data-tour-id="sidebar-nav"
            sx={{
              width: 210,
              flexShrink: 0,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 2,
              bgcolor: COLORS.surface,
              py: 1,
            }}
          >
            <NavEntry
              label="Overview"
              active={section === 'overview'}
              onClick={() => setSection('overview')}
            />
            <NavEntry
              label="Topology"
              active={section === 'topology'}
              onClick={() => setSection('topology')}
            />
            <NavEntry
              label="Operations"
              active={section === 'operations'}
              onClick={() => setSection('operations')}
            />
            <NavEntry
              label={
                catalogue.loading && !discoveredKinds.length
                  ? 'All resources…'
                  : `All resources (${allKinds.length})`
              }
              active={false}
              onClick={() => setKindPickerOpen(true)}
            />
            {pinnedKinds.length > 0 && (
              <Box sx={{ borderBottom: `1px solid ${COLORS.border}`, pb: 1 }}>
                <Typography
                  sx={{
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    color: COLORS.muted,
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                  }}
                >
                  PINNED
                </Typography>
                {pinnedKinds
                  .map(id => allKinds.find(kind => kind.id === id))
                  .filter((kind): kind is KindDef => Boolean(kind))
                  .map(kind => (
                    <NavEntry
                      key={`pinned-${kind.id}`}
                      label={kind.label}
                      active={section === kind.id}
                      onClick={() => {
                        setSection(kind.id);
                        setSearch('');
                        setLabelSelectorInput('');
                        setStatusFilter(new Set());
                        setSelection(null);
                        setDrawerTab('');
                      }}
                      pinned
                      onTogglePin={() => togglePinnedKind(kind.id)}
                    />
                  ))}
              </Box>
            )}
            {favorites.length > 0 && (
              <Box sx={{ borderBottom: `1px solid ${COLORS.border}`, pb: 1 }}>
                <Typography
                  sx={{
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    color: COLORS.muted,
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                  }}
                >
                  FAVORITES
                </Typography>
                {favorites.map(favorite => (
                    <Box
                      component="button"
                      type="button"
                      key={favorite.key}
                      onClick={() => openFavorite(favorite)}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 2,
                        py: 0.6,
                        border: 0,
                        bgcolor: 'transparent',
                        color: COLORS.text,
                        cursor: 'pointer',
                        textAlign: 'left',
                        '&:hover': { bgcolor: '#ffffff0a' },
                      }}
                    >
                      <StarIcon sx={{ color: COLORS.amber, fontSize: 15 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 12 }}>
                          {favorite.name}
                        </Typography>
                        <Typography
                          noWrap
                          sx={{ color: COLORS.muted, fontSize: 9.5 }}
                        >
                          {favorite.kind} ·{' '}
                          {favorite.namespace || favorite.cluster}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
              </Box>
            )}
            {visibleKindGroups.map(group => {
              const open = openSections[group.section] ?? false;
              return (
                <Box key={group.section}>
                  <Box
                    onClick={() =>
                      setOpenSections(state => ({
                        ...state,
                        [group.section]: !open,
                      }))
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      pt: 1.25,
                      pb: 0.5,
                      cursor: 'pointer',
                      '&:hover .k8s-nav-section': { color: COLORS.text },
                    }}
                  >
                    <Typography
                      className="k8s-nav-section"
                      sx={{
                        flex: 1,
                        color: COLORS.muted,
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: 0.6,
                      }}
                    >
                      {group.section.toUpperCase()}
                    </Typography>
                    {open ? (
                      <KeyboardArrowUpIcon
                        sx={{ fontSize: 16, color: COLORS.muted }}
                      />
                    ) : (
                      <KeyboardArrowDownIcon
                        sx={{ fontSize: 16, color: COLORS.muted }}
                      />
                    )}
                  </Box>
                  <Collapse in={open}>
                    {group.items.map(item => (
                      <NavEntry
                        key={item.id}
                        label={item.label}
                        active={section === item.id}
                        onClick={() => {
                          setSection(item.id);
                          setSearch('');
                          setLabelSelectorInput('');
                          setStatusFilter(new Set());
                          setSelection(null);
                          setDrawerTab('');
                        }}
                        pinned={pinnedKinds.includes(item.id)}
                        onTogglePin={() => togglePinnedKind(item.id)}
                      />
                    ))}
                  </Collapse>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {clusterInfo &&
            !clusterInfo.connected &&
            clusterInfo.error?.includes('not authorized') ? (
              <OopsPanel detail={`${cluster}: ${clusterInfo.error}`} />
            ) : !namespace && activeKind?.namespaced !== false ? (
              <Alert severity="info">
                Select a project (namespace) to browse its resources.
              </Alert>
            ) : section === 'overview' ? (
              <OverviewPanel
                cluster={cluster}
                clusterInfo={clusterInfo}
                namespace={namespace}
              />
            ) : section === 'topology' ? (
              <TopologyPanel
                api={api}
                cluster={cluster}
                namespace={namespace}
                highlighted={
                  selection
                    ? {
                        kind: selection.kind.kind,
                        name: selection.object.metadata.name,
                      }
                    : undefined
                }
                onOpen={(targetSection, object) => {
                  const targetKind = allKinds.find(
                    kind => kind.id === targetSection,
                  );
                  if (!targetKind) return;
                  if (!openResource(targetKind, object)) return;
                  setSection(targetSection);
                }}
              />
            ) : section === 'operations' ? (
              <OperationsCenter
                api={api}
                cluster={cluster}
                namespace={namespace}
                tab={opsTab}
                onTabChange={setOpsTab}
                resource={
                  selection
                    ? {
                        kind: selection.kind.kind,
                        resource: selection.kind.plural,
                        apiBase: selection.kind.apiBase,
                        object: selection.object,
                      }
                    : undefined
                }
              />
            ) : !activeKind ? (
              catalogue.loading ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={16} />
                  <Typography sx={{ color: COLORS.muted, fontSize: 12.5 }}>
                    Reading the cluster's resource catalogue…
                  </Typography>
                </Stack>
              ) : (
                <Alert severity="info">
                  <strong>{section}</strong> is not a resource type served by{' '}
                  {cluster || 'this cluster'}. It may belong to an operator
                  that is not installed here — pick another from{' '}
                  <strong>All resources</strong>.
                </Alert>
              )
            ) : (
              (
                <Box>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 1.5 }}
                  >
                    <Typography
                      sx={{ color: COLORS.text, fontSize: 15, fontWeight: 700 }}
                    >
                      {activeKind.label}
                    </Typography>
                    <Chip
                      size="small"
                      label={filtered.length}
                      sx={{
                        height: 20,
                        fontSize: 11,
                        bgcolor: `${COLORS.blue}12`,
                        color: COLORS.blue,
                      }}
                    />
                    <Tooltip
                      title={
                        resourceWatch.error ??
                        (resourceWatch.connected
                          ? 'Live Kubernetes watch connected'
                          : 'Using request-based refresh')
                      }
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          resourceWatch.connected
                            ? 'Live'
                            : resourceWatch.reconnecting
                            ? 'Reconnecting'
                            : 'Refresh'
                        }
                        sx={{
                          height: 20,
                          fontSize: 10,
                          color: resourceWatch.connected
                            ? COLORS.green
                            : resourceWatch.reconnecting
                            ? COLORS.amber
                            : COLORS.muted,
                          borderColor: resourceWatch.connected
                            ? `${COLORS.green}88`
                            : resourceWatch.reconnecting
                            ? `${COLORS.amber}88`
                            : COLORS.border,
                        }}
                      />
                    </Tooltip>
                    <Box sx={{ flex: 1 }} />
                    {activeKind.id !== 'pods' && (
                      <TextField
                        size="small"
                        placeholder="Label selector (app=foo,tier=backend)"
                        value={labelSelectorInput}
                        onChange={event =>
                          setLabelSelectorInput(event.target.value)
                        }
                        error={Boolean(labelSelectorError)}
                        helperText={labelSelectorError ?? undefined}
                        sx={{
                          width: 260,
                          '& .MuiFormHelperText-root': {
                            position: 'absolute',
                            top: '100%',
                            whiteSpace: 'nowrap',
                            fontSize: 10.5,
                          },
                        }}
                      />
                    )}
                    <TextField
                      size="small"
                      placeholder="Filter by name"
                      value={search}
                      onChange={event => setSearch(event.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              fontSize="small"
                              sx={{ color: COLORS.muted }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: 260 }}
                    />
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{ mb: 1.5 }}
                  >
                    <Typography
                      sx={{ color: COLORS.muted, fontSize: 11, mr: 0.5 }}
                    >
                      Status:
                    </Typography>
                    {STATUS_BUCKETS.map(bucket => {
                      const active = statusFilter.has(bucket.id);
                      return (
                        <Chip
                          key={bucket.id}
                          size="small"
                          clickable
                          label={bucket.label}
                          onClick={() =>
                            setStatusFilter(current => {
                              const next = new Set(current);
                              if (next.has(bucket.id)) next.delete(bucket.id);
                              else next.add(bucket.id);
                              return next;
                            })
                          }
                          sx={{
                            height: 22,
                            fontSize: 11,
                            color: active ? '#101010' : bucket.color,
                            bgcolor: active
                              ? bucket.color
                              : `${bucket.color}18`,
                            border: `1px solid ${bucket.color}55`,
                            fontWeight: active ? 700 : 400,
                          }}
                        />
                      );
                    })}
                    {statusFilter.size > 0 && (
                      <Button
                        size="small"
                        onClick={() => setStatusFilter(new Set())}
                        sx={{
                          textTransform: 'none',
                          color: COLORS.muted,
                          fontSize: 11,
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Stack>

                  {listError?.status === 401 || listError?.status === 403 ? (
                    <OopsPanel detail={listError.message} />
                  ) : listError?.status === 404 ? (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      {activeKind.kind} is not available on {cluster} — the{' '}
                      {activeKind.group || 'core'} API group is not served there
                      (operator/CRD not installed).
                    </Alert>
                  ) : (
                    <>
                      {listError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                          {listError.message}
                        </Alert>
                      )}

                      <TableContainer
                        sx={{
                          border: `1px solid ${COLORS.border}`,
                          borderRadius: 2,
                          bgcolor: COLORS.surface,
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{ color: COLORS.muted, fontSize: 11 }}
                              >
                                <TableSortLabel
                                  active={sortBy === 'Name'}
                                  direction={
                                    sortBy === 'Name' ? sortDir : 'asc'
                                  }
                                  onClick={() => toggleSort('Name')}
                                >
                                  Name
                                </TableSortLabel>
                              </TableCell>
                              {activeKind.columns.map(column => (
                                <TableCell
                                  key={column.label}
                                  sx={{ color: COLORS.muted, fontSize: 11 }}
                                >
                                  {column.sortValue ? (
                                    <TableSortLabel
                                      active={sortBy === column.label}
                                      direction={
                                        sortBy === column.label
                                          ? sortDir
                                          : 'asc'
                                      }
                                      onClick={() => toggleSort(column.label)}
                                    >
                                      {column.label}
                                    </TableSortLabel>
                                  ) : (
                                    column.label
                                  )}
                                </TableCell>
                              ))}
                              <TableCell
                                sx={{ width: 40, color: COLORS.muted }}
                              />
                            </TableRow>
                          </TableHead>
                          <TableBody ref={tableBodyRef}>
                            {rows.padTop > 0 && (
                              <TableRow
                                aria-hidden
                                sx={{ height: rows.padTop }}
                              >
                                <TableCell
                                  colSpan={activeKind.columns.length + 2}
                                  sx={{ p: 0, border: 0 }}
                                />
                              </TableRow>
                            )}
                            {filtered.slice(rows.start, rows.end).map(item => (
                              <TableRow
                                key={item.metadata.uid ?? item.metadata.name}
                                hover
                                sx={{
                                  cursor: 'pointer',
                                  transition: 'background-color 1.2s ease',
                                  bgcolor: resourceWatch.changed.has(
                                    item.metadata.uid ?? item.metadata.name,
                                  )
                                    ? `${COLORS.blue}22`
                                    : undefined,
                                }}
                                onClick={() => openResource(activeKind, item)}
                              >
                                <TableCell
                                  sx={{
                                    color: COLORS.blue,
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {item.metadata.name}
                                </TableCell>
                                {activeKind.columns.map(column => (
                                  <TableCell
                                    key={column.label}
                                    sx={{ color: COLORS.text, fontSize: 12 }}
                                  >
                                    {column.render(item, columnContext)}
                                  </TableCell>
                                ))}
                                <TableCell
                                  sx={{ width: 40 }}
                                  onClick={event => event.stopPropagation()}
                                >
                                  <RowActionsMenu
                                    api={api}
                                    cluster={cluster}
                                    namespace={
                                      item.metadata.namespace ??
                                      (activeKind.namespaced === false
                                        ? ''
                                        : namespace)
                                    }
                                    kind={activeKind}
                                    object={item}
                                    favorite={favorites.some(
                                      f =>
                                        favoriteKey(f) ===
                                        favoriteKey({
                                          cluster,
                                          namespace:
                                            item.metadata.namespace ??
                                            (activeKind.namespaced === false
                                              ? ''
                                              : namespace),
                                          section: activeKind.id,
                                          name: item.metadata.name,
                                        }),
                                    )}
                                    onToggleFavorite={() =>
                                      toggleFavorite({
                                        cluster,
                                        namespace:
                                          item.metadata.namespace ??
                                          (activeKind.namespaced === false
                                            ? ''
                                            : namespace),
                                        section: activeKind.id,
                                        kind: activeKind.kind,
                                        name: item.metadata.name,
                                      })
                                    }
                                    onOpen={() => openResource(activeKind, item)}
                                    onChanged={refresh}
                                    notify={setToast}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                            {rows.padBottom > 0 && (
                              <TableRow
                                aria-hidden
                                sx={{ height: rows.padBottom }}
                              >
                                <TableCell
                                  colSpan={activeKind.columns.length + 2}
                                  sx={{ p: 0, border: 0 }}
                                />
                              </TableRow>
                            )}
                            {!loading && filtered.length === 0 && (
                              <TableRow>
                                <TableCell
                                  colSpan={activeKind.columns.length + 2}
                                  sx={{ color: COLORS.muted, fontSize: 12 }}
                                >
                                  No {activeKind.label.toLowerCase()} found{' '}
                                  {activeKind.namespaced === false
                                    ? `on ${cluster}`
                                    : `in ${namespace}`}
                                  .
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ mt: 1, minHeight: 30 }}
                      >
                        <Typography
                          sx={{ color: COLORS.muted, fontSize: 11.5 }}
                        >
                          {filtered.length === items.length
                            ? `${items.length} ${
                                items.length === 1
                                  ? activeKind.kind
                                  : activeKind.label.toLowerCase()
                              }`
                            : `${filtered.length} of ${items.length} shown`}
                          {rows.active && ` · ${rows.end - rows.start} rendered`}
                        </Typography>
                        {listMore && (
                          <>
                            <Chip
                              size="small"
                              label={
                                listMore.remaining
                                  ? `${listMore.remaining} more on the cluster`
                                  : 'More on the cluster'
                              }
                              sx={{
                                height: 20,
                                fontSize: 10.5,
                                color: COLORS.amber,
                                bgcolor: `${COLORS.amber}18`,
                                border: `1px solid ${COLORS.amber}55`,
                              }}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={loadingMore}
                              startIcon={
                                loadingMore ? (
                                  <CircularProgress size={12} />
                                ) : undefined
                              }
                              onClick={loadMore}
                              sx={{ textTransform: 'none', fontSize: 11.5 }}
                            >
                              {loadingMore ? 'Loading…' : 'Load more'}
                            </Button>
                          </>
                        )}
                      </Stack>
                    </>
                  )}
                </Box>
              )
            )}
          </Box>
        </Stack>

        {selection &&
          cluster &&
          (namespace || selection.kind.namespaced === false) && (
            <DetailDrawer
              key={selection.kind.id}
              cluster={cluster}
              namespace={namespace}
              selection={selection}
              tab={drawerTab || 'details'}
              onTabChange={setDrawerTab}
              onClose={() => {
                setSelection(null);
                setDrawerTab('');
              }}
              onChanged={refresh}
              notify={setToast}
              onNavigateToNode={navigateToNode}
              onNavigateToPod={navigateToPod}
              favorite={favorites.some(
                favorite =>
                  favoriteKey(favorite) ===
                  favoriteKey({
                    cluster,
                    namespace:
                      selection.object.metadata.namespace ??
                      (selection.kind.namespaced === false ? '' : namespace),
                    section: selection.kind.id,
                    name: selection.object.metadata.name,
                  }),
              )}
              onToggleFavorite={() =>
                toggleFavorite({
                  cluster,
                  namespace:
                    selection.object.metadata.namespace ??
                    (selection.kind.namespaced === false ? '' : namespace),
                  section: selection.kind.id,
                  kind: selection.kind.kind,
                  name: selection.object.metadata.name,
                })
              }
            />
          )}

        <GlobalSearchDialog
          api={api}
          cluster={cluster}
          open={globalSearchOpen}
          onClose={() => setGlobalSearchOpen(false)}
          onSelect={openSearchResult}
        />
        <KindPicker
          open={kindPickerOpen}
          kinds={allKinds}
          loading={catalogue.loading}
          error={catalogue.error}
          onClose={() => setKindPickerOpen(false)}
          onSelect={kind => {
            setKindPickerOpen(false);
            setSection(kind.id);
            setSelection(null);
            setDrawerTab('');
            setSearch('');
            setLabelSelectorInput('');
            setStatusFilter(new Set());
          }}
        />
        <CommandPalette
          open={commandPaletteOpen}
          commands={commands}
          onClose={() => setCommandPaletteOpen(false)}
        />
        {noraOpen && (
          <NoraDock
            context={{
              cluster,
              namespace: selection?.object.metadata.namespace ?? namespace,
              kind: selection?.kind.kind,
              name: selection?.object.metadata.name,
            }}
            onClose={() => setNoraOpen(false)}
            onResourceSelect={openNoraResource}
          />
        )}

        <Dialog
          open={importOpen}
          onClose={() => !importBusy && setImportOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Import YAML</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontSize: 13, mb: 1.5 }}>
              Create resources on <strong>{cluster}</strong> by pasting one or
              more YAML documents (separated by <code>---</code>). Namespaced
              resources without a <code>metadata.namespace</code> are created in{' '}
              <strong>{namespace}</strong>. Everything runs with your own
              permissions.
            </DialogContentText>
            <TextField
              multiline
              fullWidth
              minRows={14}
              maxRows={24}
              placeholder={
                'apiVersion: v1\nkind: ConfigMap\nmetadata:\n  name: my-config\ndata:\n  key: value'
              }
              value={importText}
              onChange={event => setImportText(event.target.value)}
              InputProps={{
                sx: {
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 12.5,
                },
              }}
            />
            {importResults && (
              <Box sx={{ mt: 1.5 }}>
                {importResults.map((result, index) => (
                  <Stack
                    key={`${result.kind}-${result.name}-${index}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ py: 0.5 }}
                  >
                    <StatusChip
                      label={result.status < 300 ? 'Created' : 'Failed'}
                      color={result.status < 300 ? COLORS.green : COLORS.red}
                    />
                    <Typography sx={{ color: COLORS.text, fontSize: 12.5 }}>
                      {result.kind && result.name
                        ? `${result.kind}/${result.name}`
                        : 'Import'}
                      {result.namespace ? ` in ${result.namespace}` : ''}
                    </Typography>
                    {result.message && (
                      <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                        — {result.message}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={importBusy}
              onClick={() => setImportOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              disabled={importBusy || !importText.trim()}
              onClick={runImport}
              startIcon={
                importBusy ? <CircularProgress size={14} /> : undefined
              }
              sx={{ textTransform: 'none' }}
            >
              {importBusy ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={toast !== null}
          autoHideDuration={5000}
          onClose={() => setToast(null)}
          message={toast ?? ''}
        />
      </Content>
    </Page>
  );
}

function NavEntry({
  label,
  active,
  onClick,
  pinned,
  onTogglePin,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  /** Whether this kind is currently pinned to the top of the sidebar. */
  pinned?: boolean;
  /** Renders a pin toggle button when set (kind nav rows only). */
  onTogglePin?: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 0.7,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        fontSize: 12.5,
        fontWeight: active ? 700 : 400,
        color: active ? COLORS.text : COLORS.muted,
        borderLeft: active
          ? `3px solid ${COLORS.blue}`
          : '3px solid transparent',
        bgcolor: active ? `${COLORS.blue}10` : 'transparent',
        '&:hover': { bgcolor: `${COLORS.blue}08`, color: COLORS.text },
        '&:hover .k8s-nav-pin': { opacity: 1 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>{label}</Box>
      {onTogglePin && (
        <IconButton
          className="k8s-nav-pin"
          size="small"
          onClick={event => {
            event.stopPropagation();
            onTogglePin();
          }}
          sx={{
            p: 0.25,
            ml: 0.5,
            opacity: pinned ? 1 : 0,
            color: pinned ? COLORS.blue : COLORS.muted,
          }}
          title={pinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
        >
          {pinned ? (
            <PushPinIcon sx={{ fontSize: 13 }} />
          ) : (
            <PushPinOutlinedIcon sx={{ fontSize: 13 }} />
          )}
        </IconButton>
      )}
    </Box>
  );
}